import React, { useEffect, useMemo, useRef, useState } from 'react';
import { agentsAPI, analyticsAPI } from '../api';
import {
  formatRunnerAge,
  formatRunnerCount,
  formatRunnerDuration,
  formatRunnerModel,
  runnerHealthLabel,
  runnerRecordFor,
  safeRunnerText,
  safeTimestampTitle,
} from '../utils/runnerHealth';

const STORAGE_KEY = 'firma-agent-state-cache-v1';
const LEGACY_ANSWERS_KEY = 'firma-agent-answer-bucket-v1';
const DRAFTS_KEY = 'firma-agent-answer-drafts-v1';

const CORE_AGENT_NAMES = new Set([
  'CEO Agent',
  'CTO Agent',
  'Ops Agent',
  'Finance Agent',
  'Product Agent',
  'Hermes Agent',
]);

const ACTIVE_RUNNER_STATES = new Set(['queued', 'claimed', 'blocked', 'error']);

function runnerStatus(item) {
  const status = item.runner?.status || 'queued';
  if (status === 'queued' && item.runnerEligibility === 'missing-authority') return 'needs-authority';
  if (status === 'queued' && item.runnerEligibility === 'invalid-id') return 'invalid-id';
  return status;
}

function isActiveWork(item) {
  return ACTIVE_RUNNER_STATES.has(runnerStatus(item));
}

function shortText(value, max = 150) {
  const text = cleanText(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function contextSourceLabel(item) {
  if (item.type === 'manual-task') return 'Manual task';
  if (item.type === 'runner-question-relayed') return 'Z answered blocker';
  if (item.type === 'follow-up-relayed') return 'Z follow-up answer';
  if (item.type === 'answer-relayed') return 'Z answer';
  return item.from || 'Context';
}

function safeDomId(prefix, value) {
  return `${prefix}-${String(value || 'item').replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
}

function normalizeDomain(value) {
  return cleanText(value)
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '');
}

function projectPullRequestAction(project) {
  const directUrl = cleanText(project?.actionUrl);
  const text = `${directUrl} ${cleanText(project?.progress)} ${cleanText(project?.nextAction)}`;
  const match = text.match(/https:\/\/github\.com\/[a-z0-9_.-]+\/[a-z0-9_.-]+\/pull\/(\d+)/i);
  if (!match) return null;
  return { url: match[0], label: cleanText(project?.actionLabel) || `Review PR #${match[1]}` };
}

function formatMetric(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  if (number >= 1000) return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1)}k`;
  return String(Math.round(number));
}

function formatPercent(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0%';
  return `${number > 0 ? '+' : ''}${number.toFixed(0)}%`;
}

function asList(value) {
  if (Array.isArray(value)) return value.map(cleanText).filter(Boolean);
  const text = cleanText(value);
  return text ? [text] : [];
}

const answerRouting = {
  'finance-source': {
    agent: 'Finance Agent',
    supportingAgents: ['Hermes Agent', 'CTO Agent'],
    workTemplate: (answer) => `Use “${answer}” as the first finance data source. Build a read-only fetch path, identify required credentials, and keep write/payment actions gated.`,
    nextAction: 'Define the Billforcaster MCP connection details and run a read-only smoke fetch.',
    followUps: [
      'What is the exact Billforcaster MCP server/tool name or endpoint?',
      'Is there a read-only token/credential already available for Hermes?',
      'Which fields matter first: cashflow, invoices, expenses, runway, or project-level profitability?'
    ],
  },
  'access-policy': {
    agent: 'CTO Agent',
    supportingAgents: ['Ops Agent', 'Hermes Agent'],
    workTemplate: (answer) => `Treat access policy as: “${answer}”. Keep public entrypoints visible in FIRMA, but preserve each sensitive tool’s own login/session boundary.`,
    nextAction: 'Audit public links in FIRMA and keep health/fallback checks visible.',
    followUps: [
      'Should Hermes Funnel stay permanently public or only be enabled when needed?',
      'Should finance read-only dashboards be public behind login, or tailnet-only despite the general public-page policy?',
      'Do any client/project dashboards need to be hidden from the public cockpit?'
    ],
  },
  'firma-direction': {
    agent: 'CEO Agent',
    supportingAgents: ['Product Agent', 'CTO Agent'],
    workTemplate: (answer) => `Use FIRMA direction as: “${answer}”. Optimize for Z’s internal operations instead of signup, onboarding, pricing, or external product polish.`,
    nextAction: 'Prioritize internal cockpit features: project inventory, service health, agent decisions, and finance read-only visibility.',
    followUps: [
      'Which internal project should FIRMA manage first after Hermes/finance: Wbench, ZMCOR, crypto lab, or client ops?',
      'Should FIRMA show client/project separation explicitly to avoid mixing internal and client data?'
    ],
  },
  'state-durability': {
    agent: 'CTO Agent',
    supportingAgents: ['Ops Agent'],
    workTemplate: (answer) => `Act on durability answer: “${answer}”. Persistent state is now mounted at /data; continue by protecting it with backup/export checks.`,
    nextAction: 'Schedule or document a small backup/export check for /data/firma-agent-state.json.',
    followUps: [
      'Should I add a scheduled backup/export job for the FIRMA agent state file?'
    ],
  },
};

const fallbackState = {
  agents: [
    {
      name: 'CEO Agent',
      role: 'Direction and trade-off owner',
      workingOn: 'Turn Z answers into business direction and operating constraints.',
      status: 'Active',
      delegatedTo: ['CTO Agent', 'Finance Agent', 'Product Agent', 'Research Agent'],
      waitingOn: 'No blocker. Converts Z direction into one next operating move.',
      skills: ['z-codex-workflow-orchestrator', 'z-agent-roster', 'traction-eos', 'writing-plans'],
      toolsets: ['session_search', 'web', 'delegation'],
      guardrails: 'Chooses priorities and asks only decision-level questions; does not execute sensitive writes.'
    },
    {
      name: 'CTO Agent',
      role: 'Architecture and delivery owner',
      workingOn: 'Keep FIRMA durable, secure, and useful as the management cockpit.',
      status: 'Active',
      delegatedTo: ['Ops Agent', 'Integration Agent', 'Security Agent', 'QA Release Agent', 'Hermes Agent'],
      waitingOn: 'No blocker. Owns persistence, access policy, and integration shape.',
      skills: ['system-design', 'clean-architecture', 'ddia-systems', 'release-it', 'software-design-philosophy'],
      toolsets: ['terminal', 'web', 'file', 'delegation'],
      guardrails: 'No deploy, DNS, credential, or destructive production change without explicit approval.'
    },
    {
      name: 'Ops Agent',
      role: 'VPS, Dokploy, domains, health',
      workingOn: 'Watch Dokploy services, public domains, container status, durable storage, and runner health.',
      status: 'Watching',
      delegatedTo: ['Security Agent', 'QA Release Agent', 'Hermes Agent'],
      waitingOn: 'No blocker. Escalates failed deploys, public access drift, or volume issues.',
      skills: ['docker-management', 'vibetraffic-dokploy-smoke-test', 'hermes-operations', 'hermes-dashboard-remote-access'],
      toolsets: ['terminal', 'web'],
      guardrails: 'Read-only checks first; restarts/redeploys only when explicitly requested or already authorized for this task.'
    },
    {
      name: 'Finance Agent',
      role: 'Read-only financial visibility',
      workingOn: 'Prepare the first finance source as a read-only integration with approval gates for writes.',
      status: 'Blocked on source access',
      delegatedTo: ['Integration Agent', 'Hermes Agent', 'Security Agent'],
      waitingOn: 'Needs exact Billforcaster MCP endpoint/tool and read-only credential name before live fetch.',
      skills: ['agentic-mcp', 'native-mcp', 'productivity-automation-integrations', 'zoho-one-marketing'],
      toolsets: ['terminal', 'web', 'mcp'],
      guardrails: 'Read-only finance summaries only. No payments, invoice sending, tax filing, deletion, or money movement without explicit Z approval.'
    },
    {
      name: 'Product Agent',
      role: 'Feature shaping and internal-tool focus',
      workingOn: 'Keep FIRMA optimized for internal project management, not public SaaS onboarding.',
      status: 'Active',
      delegatedTo: ['CEO Agent', 'UX Agent', 'Research Agent'],
      waitingOn: 'No blocker. Uses internal-only direction to cut product-surface scope.',
      skills: ['37signals-way', 'lean-startup', 'jobs-to-be-done', 'inspired-product', 'continuous-discovery'],
      toolsets: ['web', 'session_search', 'delegation'],
      guardrails: 'Cuts scope and clarifies direction; does not add product surface unless it reduces Z’s operating load.'
    },
    {
      name: 'Hermes Agent',
      role: 'Execution and automation layer',
      workingOn: 'Run scheduled agent work, maintain Hermes access, and bridge FIRMA runner items to Hermes tools.',
      status: 'Live',
      delegatedTo: ['Integration Agent', 'Ops Agent'],
      waitingOn: 'No blocker except external credentials/tools for specific integrations.',
      skills: ['hermes-agent', 'hermes-operations', 'kanban-orchestrator', 'webhook-subscriptions', 'z-codex-workflow-orchestrator'],
      toolsets: ['terminal', 'file', 'cron', 'delegation', 'web'],
      guardrails: 'Only performs side effects inside the approved runner scope; escalates writes/credentials as Waiting for Z questions.'
    },
    {
      name: 'Integration Agent',
      role: 'MCP, API, credentials, and data connectors',
      workingOn: 'Turn tools like Billforcaster, Zoho, analytics, and future APIs into read-only smoke-tested connectors.',
      status: 'Needed',
      delegatedTo: ['Security Agent', 'Hermes Agent', 'Data Agent'],
      waitingOn: 'For each connector, needs endpoint/tool name and safe credential reference, not raw secret values.',
      skills: ['agentic-mcp', 'native-mcp', 'productivity-automation-integrations', 'webhook-subscriptions', 'clean-architecture'],
      toolsets: ['terminal', 'web', 'mcp'],
      guardrails: 'Use read-only scopes first; never print or store raw secrets; convert missing credentials into Waiting for Z questions.'
    },
    {
      name: 'Security Agent',
      role: 'Access boundaries, secrets, and approval gates',
      workingOn: 'Keep public dashboards, tailnet routes, auth, secrets, and finance/write approvals safe.',
      status: 'Needed',
      delegatedTo: ['Ops Agent', 'CTO Agent'],
      waitingOn: 'No blocker. Reviews new public links and sensitive tool access before they become default paths.',
      skills: ['hermes-dashboard-remote-access', 'home-operations-and-device-support', 'release-it', 'dockerized-webapp-lan-access'],
      toolsets: ['terminal', 'web'],
      guardrails: 'Blocks secret leakage and sensitive writes. Public visibility does not remove per-tool auth requirements.'
    },
    {
      name: 'QA Release Agent',
      role: 'Build, smoke, deploy-readiness, and regression checks',
      workingOn: 'Prove changes through Docker builds, API smoke tests, live bundle checks, and Git/Dokploy status.',
      status: 'Needed',
      delegatedTo: ['Ops Agent', 'CTO Agent'],
      waitingOn: 'No blocker. Runs before claimed completion of FIRMA/Dokploy changes.',
      skills: ['github-operations', 'docker-management', 'software-debugging-workflows', 'repo-production-readiness-cron-pair', 'code-review'],
      toolsets: ['terminal', 'file', 'web'],
      guardrails: 'Does not claim success without real command/live output; no push/deploy unless explicitly authorized.'
    },
    {
      name: 'UX Agent',
      role: 'FIRMA cockpit usability and answer-window behavior',
      workingOn: 'Keep answer windows stable, reduce dashboard clutter, and make next actions obvious.',
      status: 'Needed',
      delegatedTo: ['Product Agent', 'Frontend Agent'],
      waitingOn: 'No blocker. Watches for friction like disappearing cards or unclear buttons.',
      skills: ['frontend-ui-audit-and-modernization', 'ux-heuristics', 'browser-harness', 'dogfood', 'refactoring-ui'],
      toolsets: ['browser', 'terminal', 'file'],
      guardrails: 'Favor fewer visible choices and one clear action; verify UX behavior with built assets or browser checks.'
    },
    {
      name: 'Data Agent',
      role: 'Analytics, state, schemas, and reporting',
      workingOn: 'Keep FIRMA state, agent logs, analytics, and future finance summaries consistent and queryable.',
      status: 'Needed',
      delegatedTo: ['Integration Agent', 'CTO Agent'],
      waitingOn: 'No blocker. Flags when file state should become database-backed or backed up.',
      skills: ['ddia-systems', 'system-design', 'jupyter-live-kernel', 'research-intelligence-workflows'],
      toolsets: ['terminal', 'file', 'web'],
      guardrails: 'No invented data. Read/transform/report only from verified sources.'
    },
    {
      name: 'Research Agent',
      role: 'External facts and option comparison',
      workingOn: 'Resolve tool/product/architecture choices with current evidence and one recommendation.',
      status: 'Needed',
      delegatedTo: ['CEO Agent', 'Product Agent'],
      waitingOn: 'No blocker. Activates when an agent needs current facts before proceeding.',
      skills: ['research-intelligence-workflows', 'system-design', 'obviously-awesome', 'crossing-the-chasm', 'mom-test'],
      toolsets: ['web', 'terminal'],
      guardrails: 'Cites current sources and returns one recommended action, not long option menus.'
    }
  ],
  delegations: [
    {
      from: 'CEO Agent',
      to: 'CTO Agent',
      task: 'Keep FIRMA internal-first and durable while reducing Z decision load.',
      state: 'Active',
    },
    {
      from: 'Finance Agent',
      to: 'Hermes Agent',
      task: 'Prepare a read-only Billforcaster MCP fetch path for finance data.',
      state: 'Waiting for MCP details',
    },
    {
      from: 'Product Agent',
      to: 'Frontend Agent',
      task: 'Show answered questions as agent continuation logs, not as stale bucket items.',
      state: 'Active',
    },
  ],
  questions: [
    {
      id: 'finance-source',
      owner: 'Finance Agent',
      question: 'Which finance source should be connected first?',
      why: 'Determines whether the first finance integration is MCP/API, CSV import, Zoho Books, Stripe, or bank export.',
      recommendation: 'Start read-only with one source. No payment or invoice writes yet.',
      priority: 'High',
    },
    {
      id: 'access-policy',
      owner: 'CTO Agent',
      question: 'Which dashboards should remain public, tailnet-only, or VPS-login-only?',
      why: 'Hermes and finance surfaces are sensitive; public links need clear auth boundaries.',
      recommendation: 'Public FIRMA is fine; sensitive dashboards keep their own auth. Finance writes still require approval.',
      priority: 'High',
    },
    {
      id: 'firma-direction',
      owner: 'CEO Agent',
      question: 'Is FIRMA internal ops cockpit only, or should it become a sellable product?',
      why: 'Product direction changes navigation, copy, onboarding, and security expectations.',
      recommendation: 'Keep it internal until agent-state, finance read-only, and deployment health are stable.',
      priority: 'Medium',
    },
    {
      id: 'state-durability',
      owner: 'CTO Agent',
      question: 'Should agent state get a Dokploy volume mount or move to a database next?',
      why: 'Backend persistence needs durable storage across redeploys before agents can rely on it.',
      recommendation: 'Add a Dokploy volume for AGENT_STATE_FILE first; move to DB only when the workflow grows.',
      priority: 'High',
    },
  ],
  answers: {},
  answerWork: [],
  agentLog: [],
  pendingQuestions: [],
  manualTasks: [],
  projects: [],
  projectsNeedingAttention: [],
  updatedAt: null,
  updatedBy: null,
};

const PROJECT_PRIORITY_ORDER = { High: 0, Medium: 1, Low: 2 };

function parseJSON(value, fallback) {
  try {
    return JSON.parse(value || '');
  } catch (error) {
    return fallback;
  }
}

function cleanText(value) {
  return String(value ?? '').trim();
}

export function decisionOptionsFor(question = {}) {
  const recommendation = cleanText(question.recommendation) || 'Use the safest read-only path the agent recommends.';
  const seen = new Set();
  let options = (Array.isArray(question.options) ? question.options : []).slice(0, 4).map((option, index) => {
    const source = typeof option === 'string' ? { label: option, value: option } : (option || {});
    const label = cleanText(source.label || source.text || source.value);
    const value = cleanText(source.value || source.label || source.text);
    if (!label || !value || seen.has(value.toLowerCase())) return null;
    seen.add(value.toLowerCase());
    return {
      id: cleanText(source.id) || `option-${index + 1}`,
      label,
      value,
      description: cleanText(source.description),
      recommended: Boolean(source.recommended),
    };
  }).filter(Boolean);
  if (!options.length) options.push({ id: 'recommended', label: 'Use recommended path', value: recommendation, description: recommendation, recommended: true });
  if (options.length < 2) options.push({ id: 'more-evidence', label: 'Get more evidence first', value: 'Pause and gather more evidence before continuing.', description: 'Agent returns with evidence and an updated recommendation.', recommended: false });
  let recommendedIndex = options.findIndex((option) => option.recommended);
  if (recommendedIndex < 0) recommendedIndex = 0;
  options = options.map((option, index) => ({ ...option, recommended: index === recommendedIndex }));
  return options.sort((left, right) => Number(right.recommended) - Number(left.recommended));
}

function mergeObjectsByKey(rawList, defaultList, keyFn, options = {}) {
  const rawItems = Array.isArray(rawList) ? rawList : [];
  const merged = [];
  const seen = new Set();
  const preferDefaults = Boolean(options.preferDefaults);

  defaultList.forEach((defaultItem) => {
    const key = keyFn(defaultItem);
    const rawItem = rawItems.find((item) => keyFn(item) === key);
    const next = rawItem && typeof rawItem === 'object'
      ? (preferDefaults ? { ...rawItem, ...defaultItem } : { ...defaultItem, ...rawItem })
      : defaultItem;
    merged.push(next);
    seen.add(key);
  });

  rawItems.forEach((item) => {
    const key = keyFn(item);
    if (!seen.has(key)) {
      merged.push(item);
    }
  });

  return merged;
}

function mergeAgentsWithDefaults(rawAgents) {
  // Server state wins; the bundled fallback roster only fills in agents the
  // server doesn't know about. preferDefaults here would clobber server-side
  // roster edits with whatever roster this bundle shipped with.
  return mergeObjectsByKey(rawAgents, fallbackState.agents, (item) => item?.name || '');
}

function mergeDelegationsWithDefaults(rawDelegations) {
  return mergeObjectsByKey(
    rawDelegations,
    fallbackState.delegations,
    (item) => `${item?.from || ''}->${item?.to || ''}:${item?.task || ''}`
  );
}

function mergeQuestionsWithDefaults(rawQuestions) {
  return mergeObjectsByKey(rawQuestions, fallbackState.questions, (item) => item?.id || '');
}

function followUpId(parentId, index) {
  return `${parentId}:followup:${index + 1}`;
}

function buildLocalAgentWorkflow(board) {
  const pendingQuestions = [];
  const agentLog = [];
  const answerWork = [];
  const answers = board.answers || {};

  (board.questions || []).forEach((question) => {
    const answer = cleanText(answers[question.id]);
    const route = answerRouting[question.id] || {
      agent: question.returnTo || question.owner || 'CEO Agent',
      supportingAgents: [],
      workTemplate: (value) => `Use Z’s answer “${value}” to update this agent’s current work.`,
      nextAction: 'Continue work from Z’s answer; ask again only if blocked.',
      followUps: ['What should be the first visible action from this answer?'],
    };

    if (!answer) {
      pendingQuestions.push({ ...question, stage: 'initial', parentQuestionId: null });
      return;
    }

    const workItem = {
      questionId: question.id,
      question: question.question,
      owner: question.owner,
      priority: question.priority,
      answer,
      agent: route.agent,
      supportingAgents: route.supportingAgents,
      workingOn: route.workTemplate(answer),
      nextAction: route.nextAction,
      followUpQuestions: route.followUps,
      status: 'Relayed / continuing',
    };
    answerWork.push(workItem);
    agentLog.push({
      id: `${question.id}:answer`,
      type: 'answer-relayed',
      agent: route.agent,
      from: 'Z',
      question: question.question,
      answer,
      message: `Z answered. Relayed to ${route.agent}; agent should continue working from this answer.`,
      workingOn: workItem.workingOn,
      delegatedTo: route.supportingAgents,
      nextAction: route.nextAction,
      status: 'Continuing',
      questionId: question.id,
      parentQuestionId: null,
    });

    let emittedPendingFollowUp = false;
    (route.followUps || []).forEach((followUp, index) => {
      const id = followUpId(question.id, index);
      const followUpAnswer = cleanText(answers[id]);
      if (followUpAnswer) {
        agentLog.push({
          id: `${id}:answer`,
          type: 'follow-up-relayed',
          agent: route.agent,
          from: 'Z',
          question: followUp,
          answer: followUpAnswer,
          message: `Z answered the follow-up. Relayed back to ${route.agent}; agent can continue without showing this in the bucket.`,
          workingOn: `Continue “${route.nextAction}” using Z’s follow-up answer.`,
          delegatedTo: route.supportingAgents,
          nextAction: 'Continue the work; ask another question only if blocked.',
          status: 'Continuing',
          questionId: id,
          parentQuestionId: question.id,
        });
        return;
      }

      if (!emittedPendingFollowUp) {
        pendingQuestions.push({
          id,
          owner: route.agent,
          question: followUp,
          why: `Needed so ${route.agent} can continue after Z answered “${question.question}”.`,
          recommendation: 'Answer briefly. Once saved, it is relayed into the agent log and removed from this bucket.',
          priority: question.priority || 'Medium',
          parentQuestionId: question.id,
          stage: 'follow-up',
        });
        emittedPendingFollowUp = true;
      }
    });
  });

  return { pendingQuestions, agentLog, answerWork };
}

function normalizeBoard(raw) {
  const merged = {
    ...fallbackState,
    ...(raw || {}),
    agents: mergeAgentsWithDefaults(raw?.agents),
    delegations: mergeDelegationsWithDefaults(raw?.delegations),
    questions: mergeQuestionsWithDefaults(raw?.questions),
    answers: (raw && raw.answers) || {},
    manualTasks: Array.isArray(raw?.manualTasks) ? raw.manualTasks : [],
    // Server-derived project registry; old cached states won't have these.
    projects: Array.isArray(raw?.projects) ? raw.projects : [],
    projectsNeedingAttention: Array.isArray(raw?.projectsNeedingAttention) ? raw.projectsNeedingAttention : [],
  };
  const localWorkflow = buildLocalAgentWorkflow(merged);
  return {
    ...merged,
    answerWork: Array.isArray(raw?.answerWork) ? raw.answerWork : localWorkflow.answerWork,
    agentLog: Array.isArray(raw?.agentLog) ? raw.agentLog : localWorkflow.agentLog,
    pendingQuestions: Array.isArray(raw?.pendingQuestions) ? raw.pendingQuestions : localWorkflow.pendingQuestions,
    unprocessedAgentLog: Array.isArray(raw?.unprocessedAgentLog) ? raw.unprocessedAgentLog : localWorkflow.unprocessedAgentLog,
  };
}

function loadCachedState() {
  const cached = parseJSON(localStorage.getItem(STORAGE_KEY), null);
  if (cached) {
    return normalizeBoard(cached);
  }

  const legacyAnswers = parseJSON(localStorage.getItem(LEGACY_ANSWERS_KEY), {});
  return normalizeBoard({ ...fallbackState, answers: legacyAnswers || {} });
}

function cacheState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  localStorage.setItem(LEGACY_ANSWERS_KEY, JSON.stringify(state.answers || {}));
}

function loadDrafts() {
  return parseJSON(localStorage.getItem(DRAFTS_KEY), {}) || {};
}

function cacheDrafts(drafts) {
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts || {}));
}

function TokenList({ items, variant = 'skill' }) {
  if (!items?.length) return null;

  return (
    <ul className={`agent-token-list ${variant}`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function AgentCard({ agent }) {
  const hasDelegations = agent.delegatedTo?.length;
  const hasDetails = agent.skills?.length || agent.toolsets?.length || agent.guardrails;
  const defaultModel = formatRunnerModel(agent.defaultModel);
  const escalationModel = formatRunnerModel(agent.escalationModel);
  const reasoningEffort = safeRunnerText(agent.reasoningEffort);

  return (
    <article className="agent-card compact" key={agent.name}>
      <div className="agent-card-header">
        <div>
          <h3>{agent.name}</h3>
          <span>{agent.role}</span>
        </div>
        <span className="agent-status">{agent.status}</span>
      </div>

      <p className="agent-focus">{agent.workingOn}</p>

      <div className="agent-model-policy" aria-label={`${agent.name} model policy`}>
        {defaultModel ? (
          <>
            <span>{defaultModel}</span>
            <span>{reasoningEffort || 'Reasoning pending'}</span>
            {escalationModel ? <span>Escalates: {escalationModel}</span> : null}
          </>
        ) : <span>Model policy pending</span>}
      </div>

      <div className="agent-meta-row">
        <strong>Can coordinate with:</strong>
        <span>{hasDelegations ? agent.delegatedTo.join(', ') : 'No configured specialists'}</span>
      </div>

      <div className="agent-meta-row waiting">
        <strong>Blocked by:</strong>
        <span>{agent.waitingOn}</span>
      </div>

      {hasDetails ? (
        <details className="agent-detail-drawer">
          <summary>Skills, tools, guardrail</summary>
          {agent.skills?.length ? (
            <div className="agent-skills-block">
              <strong>Skills</strong>
              <TokenList items={agent.skills} />
            </div>
          ) : null}
          {agent.toolsets?.length ? (
            <div className="agent-skills-block toolsets">
              <strong>Tool lanes</strong>
              <TokenList items={agent.toolsets} variant="toolset" />
            </div>
          ) : null}
          {agent.guardrails ? (
            <div className="agent-guardrail">
              <strong>Guardrail:</strong> {agent.guardrails}
            </div>
          ) : null}
        </details>
      ) : null}
    </article>
  );
}

function RunnerBadge({ status }) {
  const labels = { 'needs-authority': 'Needs safe authority', 'invalid-id': 'Invalid work ID' };
  return <span className={`runner-state-badge ${status || 'queued'}`}>{labels[status] || status || 'queued'}</span>;
}

export function CommunicationRoute({ from = 'FIRMA', agent = 'Agent', to = '' }) {
  return <div className="op-flow">{from} → <b>{agent}</b>{to ? <> → <b>{to}</b></> : null}</div>;
}

export function LinkedHandoffRoute({ item }) {
  const report = handoffReportFor(item);
  if (!report.createdTaskIds.length || (!report.handoffTo && !report.continuationAgent)) return null;
  return (
    <div className="delegation-route">
      <span>{item.agent}</span>
      <strong>{report.continuationAgent ? '↻' : '→'}</strong>
      <span>{report.continuationAgent || report.handoffTo}</span>
      {report.continuationAgent ? <small>automatic</small> : null}
    </div>
  );
}

function HandoffReportCard({ item, compact = false, onCopy }) {
  const report = handoffReportFor(item);
  const hasReport = report.summary || report.whatMatters.length || report.evidence.length || report.nextAction || report.blockedBy;
  if (!hasReport) return null;

  return (
    <article className={`handoff-report-card ${compact ? 'compact' : ''}`}>
      <div className="handoff-report-header">
        <div>
          <span className="context-source">Agent result</span>
          <h4>{item.question}</h4>
        </div>
        <RunnerBadge status={report.status} />
      </div>

      <dl className="handoff-report-grid">
        <div>
          <dt>What matters</dt>
          <dd>{report.whatMatters.length ? report.whatMatters.map((entry) => shortText(entry, 220)).join(' • ') : 'No summary provided.'}</dd>
        </div>
        <div>
          <dt>Evidence</dt>
          <dd>{report.evidence.length ? report.evidence.slice(0, 4).map((entry) => shortText(entry, 180)).join(' • ') : 'No evidence listed.'}</dd>
        </div>
        <div>
          <dt>Blocked by</dt>
          <dd>{report.blockedBy || 'Nothing listed.'}</dd>
        </div>
        <div>
          <dt>Next action</dt>
          <dd>{report.nextAction || 'No next action listed.'}</dd>
        </div>
      </dl>

      {report.handoffTo ? (
        <p className="handoff-target"><strong>Handoff:</strong> {report.handoffTo}</p>
      ) : null}
      {report.continuationAgent ? (
        <p className="handoff-target"><strong>Continues automatically:</strong> {report.continuationAgent}</p>
      ) : null}

      {onCopy ? (
        <div className="handoff-actions">
          <button type="button" className="followup-btn" onClick={() => onCopy(item)}>Copy report</button>
        </div>
      ) : null}
    </article>
  );
}

const PROJECT_STATUS_OPTIONS = ['deployed', 'development', 'monitoring', 'paused'];

function projectDraftFrom(project) {
  return {
    goal: cleanText(project.goal),
    progress: cleanText(project.progress),
    nextAction: cleanText(project.nextAction),
    priority: project.priority || 'Medium',
    status: project.status || 'development',
    ownerAgent: project.ownerAgent || 'CEO Agent',
  };
}

function resultObjectFor(item) {
  const result = item?.runner?.result;
  if (typeof result === 'string') return { summary: cleanText(result) };
  if (result && typeof result === 'object') return result;
  return {};
}

export function handoffReportFor(item) {
  const result = resultObjectFor(item);
  const summary = cleanText(result.summary || result.message || result.outcome || result.nextAction);
  const evidenceSource = result.evidence || result.sources || result.proof || [];
  const whatMatters = asList(result.whatMatters || result.keyPoints || result.decisions);
  const evidence = asList(evidenceSource);
  const verification = asList(result.verification || result.commandsRun || result.testsRun);
  const risks = asList(result.risks || result.risk);
  const nextAction = cleanText(result.nextAction || item?.nextAction);
  const blockedBy = cleanText(result.blockedBy || result.blocker);
  const createdTaskIds = asList(result.createdTaskIds || item?.runner?.createdTaskIds || item?.createdTaskIds);
  const progressionType = cleanText(result.progressionType);
  const continuationAgent = createdTaskIds.length && progressionType === 'continuation'
    ? cleanText(result.continuationAgent || item?.agent)
    : '';
  // `delegatedTo` is an agent capability roster, not proof that work moved.
  // Show a handoff only when the server created linked child task IDs.
  const handoffTo = createdTaskIds.length && !continuationAgent
    ? cleanText(result.handoffTo || item?.runner?.returnTo || item?.handoffTo)
    : '';
  const outcomeType = cleanText(result.outcomeType || item?.runner?.outcomeType || item?.outcomeType);
  const goalStatus = cleanText(result.goalStatus || item?.runner?.goalStatus || item?.goalStatus);

  return {
    status: cleanText(result.status) || runnerStatus(item),
    summary,
    whatMatters: whatMatters.length ? whatMatters : (summary ? [summary] : []),
    evidence,
    verification,
    risks,
    blockedBy,
    nextAction,
    handoffTo,
    continuationAgent,
    continueAutomatically: result.continueAutomatically === true,
    progressionType,
    outcomeType,
    goalStatus,
    createdTaskIds,
  };
}

function resultSummaryFor(item) {
  return handoffReportFor(item).summary;
}

function resultEvidenceFor(item) {
  return handoffReportFor(item).evidence;
}

function formatHandoffReport(item) {
  const report = handoffReportFor(item);
  const lines = [
    `Status: ${report.status || runnerStatus(item)}`,
    `What matters: ${report.whatMatters.length ? report.whatMatters.join(' | ') : 'No summary provided.'}`,
    `Evidence: ${report.evidence.length ? report.evidence.join(' | ') : 'No evidence listed.'}`,
    `Blocked by: ${report.blockedBy || 'Nothing listed.'}`,
    `Next action: ${report.nextAction || 'No next action listed.'}`,
  ];
  if (report.handoffTo) lines.push(`Handoff to: ${report.handoffTo}`);
  if (report.continuationAgent) lines.push(`Continues automatically: ${report.continuationAgent}`);
  if (report.outcomeType) lines.push(`Outcome: ${report.outcomeType}`);
  if (report.goalStatus) lines.push(`Goal status: ${report.goalStatus}`);
  if (report.createdTaskIds.length) lines.push(`Created tasks: ${report.createdTaskIds.join(' | ')}`);
  if (report.verification.length) lines.push(`Verification: ${report.verification.join(' | ')}`);
  if (report.risks.length) lines.push(`Risks: ${report.risks.join(' | ')}`);
  return lines.join('\n');
}

function formatProjectReport(project, dossier) {
  const latest = dossier.completed[0];
  const latestReport = latest ? handoffReportFor(latest) : null;
  return [
    `Project: ${project.name}`,
    `Status: ${project.status || 'development'} / ${project.priority || 'Medium'}`,
    `Owner: ${project.ownerAgent || 'CEO Agent'}`,
    `Goal: ${cleanText(project.goal) || 'No goal defined.'}`,
    `Progress: ${cleanText(project.progress) || 'No progress recorded.'}`,
    `Current next action: ${cleanText(project.nextAction) || 'No next action queued.'}`,
    `Work state: ready ${dossier.metrics.queued}, working ${dossier.metrics.working}, blocked ${dossier.metrics.blocked}, done ${dossier.metrics.done}`,
    `Latest result: ${latestReport?.summary || 'No completed result yet.'}`,
    `Suggested next: ${latestReport?.nextAction || cleanText(project.nextAction) || 'No suggested next action.'}`,
  ].join('\n');
}

function projectItemTime(item) {
  return Date.parse(item?.runner?.finishedAt || item?.runner?.updatedAt || item?.createdAt || 0) || 0;
}

function projectKeyForItem(item) {
  return cleanText(item?.project || item?.projectName || item?.question?.project || item?.question?.projectName);
}

function threadKeyForItem(item) {
  if (cleanText(item?.rootThreadId)) return cleanText(item.rootThreadId);
  const project = projectKeyForItem(item);
  if (project) return `thread:project:${project}`;
  return `thread:log:${cleanText(item?.rootLogId || item?.parentLogId || item?.id || 'general')}`;
}

function displayStageForItem(item) {
  const status = runnerStatus(item);
  if (item.kind === 'question') return 'Waiting approval';
  if (status === 'claimed') return 'Working';
  if (status === 'queued') return cleanText(item.stage) || 'Exploring';
  if (['blocked', 'error'].includes(status)) return 'Blocked';
  const report = handoffReportFor(item);
  if (report.goalStatus) return report.goalStatus;
  if (report.nextAction) return 'Recommendation ready';
  return cleanText(item.stage) || 'Packet complete';
}

function threadStatusFor(thread) {
  if (thread.waiting.length) return { tone: 'needsyou', label: 'Waiting approval' };
  const latestItem = [...thread.items].sort((a, b) => projectItemTime(b) - projectItemTime(a))[0] || null;
  const latestStatus = latestItem ? runnerStatus(latestItem) : '';
  if (latestStatus === 'error') return { tone: 'blocked', label: 'Runner error' };
  if (latestStatus === 'blocked') return { tone: 'blocked', label: 'Blocked' };
  if (latestStatus === 'claimed') return { tone: 'working', label: 'Working' };
  if (latestStatus === 'queued') return { tone: 'ready', label: 'Exploring' };
  const latestReport = thread.latestCompleted ? handoffReportFor(thread.latestCompleted) : null;
  if ((latestReport?.goalStatus || '').toLowerCase().includes('achieved')) return { tone: 'done', label: 'Goal achieved' };
  if (latestReport?.nextAction || latestReport?.summary) return { tone: 'medium', label: 'Recommendation ready' };
  return { tone: 'done', label: 'Packet complete' };
}

function buildExplorationThreads(agentLog, pendingQuestions, projects, analyticsByDomain) {
  const projectMap = new Map((projects || []).map((project) => [project.id, project]));
  const byName = new Map((projects || []).map((project) => [cleanText(project.name).toLowerCase(), project]));
  const groups = new Map();

  const ensure = (item) => {
    const key = threadKeyForItem(item);
    if (!groups.has(key)) {
      const projectId = projectKeyForItem(item);
      const project = projectMap.get(projectId) || byName.get(projectId.toLowerCase()) || null;
      groups.set(key, {
        id: key,
        title: project?.name || cleanText(item.projectName || item.question || item.title || item.id) || 'General exploration',
        project,
        projectId: project?.id || projectId || null,
        items: [],
        waiting: [],
      });
    }
    return groups.get(key);
  };

  (agentLog || []).forEach((item) => {
    const thread = ensure(item);
    thread.items.push(item);
  });
  (pendingQuestions || []).forEach((question) => {
    const item = { ...question, kind: 'question', id: question.id, question: question.question, project: question.project, projectName: question.projectName };
    const thread = ensure(item);
    thread.waiting.push(item);
  });

  return Array.from(groups.values()).map((thread) => {
    const itemsByTime = [...thread.items].sort((a, b) => projectItemTime(b) - projectItemTime(a));
    const completed = itemsByTime.filter((item) => runnerStatus(item) === 'completed');
    const latestCompleted = completed[0] || null;
    const latestItem = itemsByTime[0] || thread.waiting[0] || null;
    const report = latestCompleted ? handoffReportFor(latestCompleted) : null;
    const agents = Array.from(new Set(thread.items.map((item) => item.agent).filter(Boolean)));
    const specialistAgents = agents.filter((agent) => agent !== 'CEO Agent');
    const branchCount = specialistAgents.length;
    const analytics = thread.project ? analyticsByDomain.get(normalizeDomain(thread.project.domain)) : null;
    const status = threadStatusFor({ ...thread, latestCompleted });
    return {
      ...thread,
      items: itemsByTime,
      completed,
      latestCompleted,
      latestItem,
      report,
      agents,
      specialistAgents,
      branchCount,
      analytics,
      status,
    };
  }).sort((a, b) => projectItemTime(b.latestItem) - projectItemTime(a.latestItem)).slice(0, 8);
}

function buildProjectDossier(project, agentLog, pendingQuestions) {
  if (!project) return null;
  const projectItems = (agentLog || [])
    .filter((item) => item.project === project.id || item.projectName === project.name)
    .sort((a, b) => projectItemTime(b) - projectItemTime(a));
  const projectItemIds = new Set(projectItems.map((item) => item.id));
  const projectQuestionIds = new Set(projectItems.map((item) => item.questionId).filter(Boolean));
  const waiting = (pendingQuestions || []).filter((question) => (
    projectItemIds.has(question.parentLogId)
    || projectQuestionIds.has(question.parentQuestionId)
  ));
  const metrics = {
    queued: projectItems.filter((item) => runnerStatus(item) === 'queued').length,
    working: projectItems.filter((item) => runnerStatus(item) === 'claimed').length,
    blocked: projectItems.filter((item) => ['blocked', 'error'].includes(runnerStatus(item))).length + waiting.length,
    done: projectItems.filter((item) => runnerStatus(item) === 'completed').length,
  };
  const completed = projectItems.filter((item) => runnerStatus(item) === 'completed');
  const active = projectItems.filter((item) => ['queued', 'claimed', 'blocked', 'error'].includes(runnerStatus(item)));
  const decisions = completed
    .filter((item) => resultSummaryFor(item) || handoffReportFor(item).createdTaskIds.length)
    .map((item) => {
      const report = handoffReportFor(item);
      return {
        id: `decision:${item.id}`,
        agent: item.agent,
        title: item.type === 'project-next-action' ? 'Owner result' : item.question,
        summary: resultSummaryFor(item) || `Created ${report.createdTaskIds.length} linked follow-up task${report.createdTaskIds.length === 1 ? '' : 's'}.`,
        time: item.runner?.finishedAt || item.runner?.updatedAt,
      };
    });
  return { projectItems, active, completed, decisions, waiting, metrics };
}

// eslint-disable-next-line no-unused-vars
function ProjectCard({ project, attention, agentNames, onSave, saving, onOpen }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => projectDraftFrom(project));

  const goal = cleanText(project.goal);
  const progress = cleanText(project.progress);
  const nextAction = cleanText(project.nextAction);

  const startEditing = () => {
    setDraft(projectDraftFrom(project));
    setEditing(true);
  };

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    const ok = await onSave({ id: project.id, name: project.name, ...draft });
    if (ok) setEditing(false);
  };

  return (
    <article
      className={`project-card ${attention ? 'needs-attention' : ''}`}
      role="button"
      tabIndex={editing ? -1 : 0}
      onClick={editing ? undefined : () => onOpen?.(project.id)}
      onKeyDown={(event) => {
        if (!editing && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onOpen?.(project.id);
        }
      }}
    >
      <div className="project-card-header">
        <div>
          <h3>{project.name}</h3>
          {project.domain ? <span className="project-domain">{project.domain}</span> : null}
        </div>
        <div className="project-chips">
          <span className={`project-status-chip ${project.status || 'development'}`}>{project.status || 'development'}</span>
          <span className={`question-priority ${(project.priority || 'medium').toLowerCase()}`}>{project.priority || 'Medium'}</span>
          {!editing ? (
            <>
              <button type="button" className="project-open-btn" onClick={(event) => { event.stopPropagation(); onOpen?.(project.id); }}>Open dossier</button>
              <button type="button" className="project-edit-btn" onClick={(event) => { event.stopPropagation(); startEditing(); }}>Edit</button>
            </>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="project-edit-form">
          <label>
            Goal
            <textarea
              value={draft.goal}
              onChange={(event) => updateDraft('goal', event.target.value)}
              placeholder="End goal in Z’s words. Empty keeps this on the CEO’s question list."
              rows={2}
            />
          </label>
          <label>
            Progress
            <textarea
              value={draft.progress}
              onChange={(event) => updateDraft('progress', event.target.value)}
              placeholder="Latest progress note."
              rows={2}
            />
          </label>
          <label>
            Next action
            <input
              value={draft.nextAction}
              onChange={(event) => updateDraft('nextAction', event.target.value)}
              placeholder="One concrete next step."
            />
          </label>
          <div className="project-edit-selects">
            <label>
              Priority
              <select value={draft.priority} onChange={(event) => updateDraft('priority', event.target.value)}>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
            </label>
            <label>
              Status
              <select value={draft.status} onChange={(event) => updateDraft('status', event.target.value)}>
                {PROJECT_STATUS_OPTIONS.map((status) => (
                  <option value={status} key={status}>{status}</option>
                ))}
              </select>
            </label>
            <label>
              Owner
              <select value={draft.ownerAgent} onChange={(event) => updateDraft('ownerAgent', event.target.value)}>
                {agentNames.map((name) => (
                  <option value={name} key={name}>{name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="project-card-actions">
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="project-edit-cancel" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="project-field">
            <strong>Goal</strong>
            {goal ? <p>{goal}</p> : <p className="project-field-empty">No goal yet — CEO will ask Z.</p>}
          </div>

          <div className="project-field">
            <strong>Progress</strong>
            {progress ? <p>{progress}</p> : <p className="project-field-empty">No progress recorded.</p>}
          </div>

          <div className="project-field">
            <strong>Next action</strong>
            {nextAction ? <p>{nextAction}</p> : <p className="project-field-missing">None queued — needs delegation.</p>}
          </div>

          {project.ownerAgent ? (
            <div className="project-owner">
              <strong>Owner:</strong> {project.ownerAgent}
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

function ProjectDetailPage({ project, dossier, onBack, onFollowUp, onCopyReport, onCopyProjectReport, onUseNextAction }) {
  const goal = cleanText(project.goal);
  const nextAction = cleanText(project.nextAction);
  const progress = cleanText(project.progress);
  const lastTouch = dossier.projectItems[0];

  return (
    <div className="agent-workspace project-detail-page">
      <section className="component-container project-detail-hero">
        <button type="button" className="project-back-btn" onClick={onBack}>← Back to cockpit</button>
        <div className="project-detail-title-row">
          <div>
            <span className="dashboard-card-badge">Project dossier</span>
            <h2>{project.name}</h2>
            {project.domain ? <a href={`https://${project.domain}`} target="_blank" rel="noreferrer">{project.domain}</a> : null}
          </div>
          <div className="project-detail-actions">
            <button type="button" className="followup-btn" onClick={() => onCopyProjectReport(project, dossier)}>Copy project report</button>
          </div>
          <div className="project-detail-chips">
            <span className={`project-status-chip ${project.status || 'development'}`}>{project.status || 'development'}</span>
            <span className={`question-priority ${(project.priority || 'medium').toLowerCase()}`}>{project.priority || 'Medium'}</span>
            <span className="delegation-state">{project.ownerAgent || 'CEO Agent'}</span>
          </div>
        </div>

        <div className="project-detail-summary-grid">
          <div>
            <strong>Goal</strong>
            <p>{goal || 'No goal defined yet — CEO should ask only if it cannot infer enough to proceed.'}</p>
          </div>
          <div>
            <strong>Current next action</strong>
            <p>{nextAction || 'No next action queued.'}</p>
          </div>
          <div>
            <strong>Latest progress</strong>
            <p>{progress || 'No progress recorded yet.'}</p>
          </div>
          <div>
            <strong>Last touch</strong>
            <p>{lastTouch ? `${lastTouch.agent} · ${runnerStatus(lastTouch)}` : 'No agent work yet.'}</p>
          </div>
        </div>

        <div className="project-dossier-metrics">
          <div><strong>{dossier.metrics.queued}</strong><span>ready</span></div>
          <div><strong>{dossier.metrics.working}</strong><span>working</span></div>
          <div><strong>{dossier.metrics.blocked}</strong><span>blocked / Z</span></div>
          <div><strong>{dossier.metrics.done}</strong><span>done</span></div>
        </div>

        <div className="project-current-execution">
          <div>
            <strong>Current execution</strong>
            <p>{dossier.active[0] ? `${dossier.active[0].agent} owns “${dossier.active[0].question}” (${runnerStatus(dossier.active[0])}).` : 'No active packet right now.'}</p>
          </div>
          <div>
            <strong>Latest handoff</strong>
            <p>{dossier.completed[0] ? shortText(resultSummaryFor(dossier.completed[0]) || handoffReportFor(dossier.completed[0]).nextAction, 260) : 'No completed handoff yet.'}</p>
          </div>
          {dossier.completed[0] && handoffReportFor(dossier.completed[0]).nextAction ? (
            <button type="button" className="followup-btn" onClick={() => onUseNextAction(project, dossier.completed[0])}>Use latest next action</button>
          ) : null}
        </div>
      </section>

      <section className="component-container project-detail-section">
        <div className="component-header">
          <div>
            <h2>Current work</h2>
            <p className="component-subtitle">Packets still queued, claimed, blocked, or errored for this project.</p>
          </div>
        </div>
        {dossier.active.length ? (
          <div className="project-detail-card-grid">
            {dossier.active.map((item) => (
              <ContextFlowCard item={item} key={item.id} />
            ))}
          </div>
        ) : (
          <div className="empty-answer-work">No active packet for this project right now.</div>
        )}
      </section>

      <section className="component-container project-detail-section">
        <div className="component-header">
          <div>
            <h2>Decisions and routing</h2>
            <p className="component-subtitle">Who routed what, which agent owns it, and why the system moved that way.</p>
          </div>
        </div>
        {dossier.decisions.length ? (
          <div className="project-decision-list">
            {dossier.decisions.map((decision) => (
              <article className="project-decision-card" key={decision.id}>
                <span>{decision.agent}</span>
                <h3>{decision.title}</h3>
                <p>{shortText(decision.summary, 520)}</p>
                {decision.time ? <time>{new Date(decision.time).toLocaleString()}</time> : null}
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-answer-work">No completed routing decision yet. The first owner packet will create one.</div>
        )}
      </section>

      <section className="component-container project-detail-section">
        <div className="component-header">
          <div>
            <h2>Results and exploration</h2>
            <p className="component-subtitle">Completed runner outputs, evidence, and follow-up options for this project.</p>
          </div>
        </div>
        {dossier.completed.length ? (
          <div className="project-results-list">
            {dossier.completed.map((item) => {
              const summary = resultSummaryFor(item);
              const evidence = resultEvidenceFor(item);
              return (
                <article className="project-result-card" key={item.id}>
                  <div className="project-result-header">
                    <div>
                      <span className="context-source">{contextSourceLabel(item)} → {item.agent}</span>
                      <h3>{item.question}</h3>
                    </div>
                    <RunnerBadge status={runnerStatus(item)} />
                  </div>
                  {summary || evidence.length ? (
                    <HandoffReportCard item={item} compact onCopy={onCopyReport} />
                  ) : (
                    <p className="result-missing-warning">Completed without a result — use Follow up to re-run with a required summary.</p>
                  )}
                  <div className="context-route">
                    <span>{item.from || 'Context'}</span>
                    <strong>→</strong>
                    <span>{item.agent}</span>
                    {item.delegatedTo?.length ? (
                      <span>Can coordinate with: {item.delegatedTo.join(', ')}</span>
                    ) : null}
                  </div>
                  <div className="followup-actions">
                    <button type="button" className="followup-btn" onClick={() => onFollowUp(item)}>Follow up</button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="empty-answer-work">No completed exploration results yet.</div>
        )}
      </section>

      <section className="component-container project-detail-section">
        <div className="component-header">
          <div>
            <h2>Waiting for Z</h2>
            <p className="component-subtitle">Only real blockers for this project should appear here.</p>
          </div>
        </div>
        {dossier.waiting.length ? (
          <div className="questions-list">
            {dossier.waiting.map((item) => (
              <article className="question-card" key={item.id}>
                <div className="question-card-header">
                  <div>
                    <span className="question-owner">{item.owner}</span>
                    <h3>{item.question}</h3>
                  </div>
                  <span className={`question-priority ${(item.priority || 'medium').toLowerCase()}`}>{item.priority || 'Medium'}</span>
                </div>
                <p><strong>Why:</strong> {item.why}</p>
                <p><strong>Recommendation:</strong> {item.recommendation}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-answer-work">No Z input needed for this project. Agents can continue independently.</div>
        )}
      </section>

      <section className="component-container project-detail-section">
        <div className="component-header">
          <div>
            <h2>Project timeline</h2>
            <p className="component-subtitle">Every packet linked to this project, newest first.</p>
          </div>
        </div>
        {dossier.projectItems.length ? (
          <div className="project-timeline-list">
            {dossier.projectItems.map((item) => (
              <article className="project-timeline-card" key={item.id}>
                <div className="project-timeline-dot" />
                <div>
                  <div className="project-result-header">
                    <div>
                      <span className="context-source">{contextSourceLabel(item)}</span>
                      <h3>{item.question}</h3>
                    </div>
                    <RunnerBadge status={runnerStatus(item)} />
                  </div>
                  <p>{shortText(resultSummaryFor(item) || item.workingOn || item.message || item.answer, 420)}</p>
                  <div className="context-route">
                    <span>{item.from || 'Context'}</span>
                    <strong>→</strong>
                    <span>{item.agent}</span>
                    {item.delegatedTo?.length ? (
                      <span>Can coordinate with: {item.delegatedTo.join(', ')}</span>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-answer-work">No project-linked packets yet.</div>
        )}
      </section>
    </div>
  );
}

function ContextFlowCard({ item, blocker, onFollowUp, onCopyReport }) {
  const status = runnerStatus(item);
  const supportAgents = item.delegatedTo || [];
  const contextText = item.answer || item.workingOn || item.message;
  const report = handoffReportFor(item);

  return (
    <article className={`context-flow-card ${status}`} id={safeDomId('work', item.id)}>
      <div className="context-flow-header">
        <div>
          <span className="context-source">{contextSourceLabel(item)}</span>
          <h3>{item.question}</h3>
        </div>
        <RunnerBadge status={status} />
      </div>

      <div className="context-route">
        <span>{item.from || 'Z'}</span>
        <strong>→</strong>
        <span>{item.agent}</span>
        {supportAgents.length ? (
          <span>Can coordinate with: {supportAgents.join(', ')}</span>
        ) : null}
      </div>

      <div className="context-packet">
        <strong>Context packet</strong>
        <p>{shortText(contextText, 220)}</p>
      </div>

      <div className="context-next-action">
        <strong>Next move</strong>
        <p>{item.nextAction}</p>
      </div>

      {report.summary || report.evidence.length || status === 'completed' ? (
        <HandoffReportCard item={item} compact onCopy={onCopyReport} />
      ) : null}

      {status === 'completed' && !report.summary ? (
        <p className="result-missing-warning">
          Completed without a result — use Follow up to re-run with a required summary.
        </p>
      ) : null}

      {status === 'completed' && onFollowUp ? (
        <div className="followup-actions">
          <button type="button" className="followup-btn" onClick={() => onFollowUp(item)}>
            Follow up
          </button>
        </div>
      ) : null}

      {blocker ? (
        <div className="context-blocker">
          <strong>Waiting for Z</strong>
          <p>{blocker.question}</p>
        </div>
      ) : null}
    </article>
  );
}

function AgentLaneCard({ lane }) {
  return (
    <article className="agent-lane-card">
      <div className="agent-lane-header">
        <div>
          <h3>{lane.agent.name}</h3>
          <span>{lane.agent.role}</span>
        </div>
        <strong>{lane.items.length}</strong>
      </div>
      {lane.items.length ? (
        <ul className="agent-lane-list">
          {lane.items.map((item) => (
            <li key={item.id}>
              <RunnerBadge status={runnerStatus(item)} />
              <span>{shortText(item.question, 100)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="agent-lane-empty">No active context packet.</p>
      )}
    </article>
  );
}

// eslint-disable-next-line no-unused-vars
function KanbanQueueColumn({ column, onSelectItem }) {
  return (
    <article className={`kanban-queue-column ${column.key}`}>
      <div className="kanban-column-header">
        <span>{column.title}</span>
        <strong>{column.items.length}</strong>
      </div>
      {column.items.length ? (
        <div className="kanban-card-list">
          {column.items.slice(0, 5).map((item) => {
            const isQuestion = column.key === 'waiting';
            const label = item.agent || item.owner || 'Agent';
            return (
              <button
                type="button"
                className="kanban-mini-card"
                key={item.id}
                onClick={() => onSelectItem(column, item)}
              >
                <div className="kanban-mini-card-top">
                  <span>{label}</span>
                  {isQuestion ? (
                    <span className={`question-priority ${(item.priority || 'medium').toLowerCase()}`}>{item.priority || 'Medium'}</span>
                  ) : (
                    <RunnerBadge status={runnerStatus(item)} />
                  )}
                </div>
                <p>{shortText(item.question, 120)}</p>
              </button>
            );
          })}
          {column.items.length > 5 ? (
            <div className="kanban-overflow-note">+{column.items.length - 5} more</div>
          ) : null}
        </div>
      ) : (
        <p className="kanban-empty">{column.emptyText}</p>
      )}
    </article>
  );
}

// eslint-disable-next-line no-unused-vars
function NowCockpit({ recommendation, flowMetrics, onAction }) {
  return (
    <section className={`component-container now-cockpit ${recommendation.kind}`}>
      <div className="now-copy">
        <span className="dashboard-card-badge">Now</span>
        <h2>{recommendation.title}</h2>
        <p>{recommendation.detail}</p>
      </div>
      <div className="now-actions">
        <button type="button" className="btn-primary" onClick={onAction}>{recommendation.actionLabel}</button>
        <div className="now-mini-metrics">
          <span>{flowMetrics.active} active</span>
          <span>{flowMetrics.blocked} blocked/waiting</span>
          <span>{flowMetrics.completed} done</span>
        </div>
      </div>
    </section>
  );
}



function agentInitials(name) {
  return cleanText(name)
    .replace(/\bAgent\b/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'AG';
}

function statusGroupForItem(item) {
  const status = runnerStatus(item);
  if (status === 'queued') return 'ready';
  if (status === 'claimed') return 'working';
  if (['blocked', 'error'].includes(status)) return 'blocked';
  if (status === 'completed') return 'done';
  return status || 'ready';
}

function statusLabel(group) {
  return {
    needsyou: 'Needs you',
    working: 'Working',
    ready: 'Ready',
    blocked: 'Blocked',
    done: 'Done',
    clear: 'Clear',
  }[group] || group;
}

function AgentAvatar({ name, small = false }) {
  return <span className={`op-avatar ${small ? 'small' : ''}`}>{agentInitials(name)}</span>;
}

function OpPill({ children, tone = 'muted' }) {
  return <span className={`op-pill ${tone}`}>{children}</span>;
}

function OperatingKpi({ tone, number, label, onClick }) {
  return (
    <button type="button" className={`op-kpi ${tone}`} onClick={onClick}>
      <span className="op-kpi-bar" />
      <strong>{number}</strong>
      <span>{label}</span>
    </button>
  );
}

// eslint-disable-next-line no-unused-vars
function buildChatPromptForItem(item) {
  const project = cleanText(item.projectName || item.project);
  const title = cleanText(item.question || item.title || item.id);
  return project
    ? `FIRMA ${item.agent || 'Agent'}: continue ${project}; use the latest report for “${shortText(title, 90)}” and propose the single next action.`
    : `FIRMA ${item.agent || 'Agent'}: continue “${shortText(title, 90)}”; propose the single next action.`;
}

export function DecisionOptionButtons({ question, onSelect = () => {}, disabled = false }) {
  const options = decisionOptionsFor(question);
  return (
    <div className="op-decision-options" role="group" aria-label={`Options for ${question.question}`}>
      {options.map((option) => (
        <button
          type="button"
          className={`op-decision-option ${option.recommended ? 'recommended' : ''}`}
          onClick={() => onSelect(option.value)}
          disabled={disabled}
          key={option.id}
        >
          <span><strong>{option.label}</strong>{option.recommended ? <em>Recommended</em> : null}</span>
          {option.description ? <small>{option.description}</small> : null}
        </button>
      ))}
    </div>
  );
}

function NeedsYouBoard({ questions, draftAnswers, updateDraft, onReturn, returningId }) {
  if (!questions.length) {
    return (
      <section className="op-card op-needs-you empty" id="needs-you">
        <div>
          <span className="op-section-eyebrow">Needs you now</span>
          <h3>Nothing waiting for you.</h3>
          <p>Agents have the current answers and should continue from their logs.</p>
        </div>
        <OpPill tone="done">All clear</OpPill>
      </section>
    );
  }

  return (
    <section className="op-card op-needs-you" id="needs-you">
      <div className="op-section-header compact">
        <div>
          <span className="op-section-eyebrow">Needs you now</span>
          <h3>{questions.length} decision{questions.length === 1 ? '' : 's'} blocking agents</h3>
          <p>Answer here; it relays into the owning agent log and work resumes.</p>
        </div>
        <OpPill tone="needsyou">⚑ Decision</OpPill>
      </div>
      <div className="op-decision-list">
        {questions.map((item) => {
          const draftValue = draftAnswers[item.id] || '';
          const isReturning = returningId === item.id;
          return (
            <article className="op-decision" id={safeDomId('question', item.id)} key={item.id}>
              <div className="op-decision-main">
                <div className="op-decision-meta">
                  <AgentAvatar name={item.owner || item.agent || 'Agent'} />
                  <span>{item.owner || item.agent || 'Agent'}</span>
                  {item.project ? <OpPill tone="muted">{item.project}</OpPill> : null}
                  <OpPill tone={(item.priority || 'Medium').toLowerCase() === 'high' ? 'high' : 'medium'}>{item.priority || 'Medium'}</OpPill>
                </div>
                <h4>{item.question}</h4>
                {item.recommendation ? <p><strong>Agent recommends:</strong> {item.recommendation}</p> : null}
                {item.why ? <p><strong>Why:</strong> {item.why}</p> : null}
                <DecisionOptionButtons question={item} onSelect={(value) => onReturn(item, value)} disabled={isReturning} />
                <label className="op-custom-answer">
                  <span>Or type something else</span>
                  <textarea
                    value={draftValue}
                    onChange={(event) => updateDraft(item.id, event.target.value)}
                    placeholder="Write your own direction…"
                    rows={3}
                  />
                </label>
              </div>
              <div className="op-decision-actions">
                <button type="button" className="op-btn" onClick={() => onReturn(item)} disabled={isReturning || !cleanText(draftValue)}>{isReturning ? 'Sending…' : 'Send custom answer'}</button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function OperatingWorkTable({ rows, filter, onFilter, onOpen, onFollowUp, onCopy }) {
  const filters = [
    ['all', 'All'],
    ['working', 'Working'],
    ['ready', 'Ready'],
    ['needsyou', 'Needs you'],
    ['blocked', 'Blocked'],
    ['done', 'Done'],
  ];
  const visible = filter === 'all' ? rows : rows.filter((row) => row.group === filter);
  const counts = filters.reduce((acc, [key]) => {
    acc[key] = key === 'all' ? rows.length : rows.filter((row) => row.group === key).length;
    return acc;
  }, {});

  return (
    <section className="op-card op-work" id="in-flight">
      <div className="op-section-header">
        <div>
          <span className="op-section-eyebrow">Who is doing what</span>
          <h3>Unified work table</h3>
          <p>Kanban, live work map, and delegation flow in one scanable table.</p>
        </div>
        <div className="op-tabs">
          {filters.map(([key, label]) => (
            <button type="button" className={filter === key ? 'active' : ''} onClick={() => onFilter(key)} key={key}>
              {label}<span>{counts[key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="op-work-table-wrap">
        <table className="op-work-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Task & flow</th>
              <th>Project</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Steer</th>
            </tr>
          </thead>
          <tbody>
            {visible.length ? visible.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="op-agent-cell">
                    <AgentAvatar name={row.agent} />
                    <div><strong>{row.agent}</strong><span>{row.role || 'Agent lane'}</span></div>
                  </div>
                </td>
                <td>
                  <strong className="op-task-title">{row.title}</strong>
                  <CommunicationRoute from={row.from || 'FIRMA'} agent={row.agent} to={row.to} />
                  <div className="op-thread-row-meta"><span>{row.threadLabel}</span><span>{row.stage}</span></div>
                  {row.summary ? <p className="op-row-summary">{shortText(row.summary, 180)}</p> : null}
                </td>
                <td>{row.project ? <OpPill tone="muted">{row.project}</OpPill> : <span className="op-muted">—</span>}</td>
                <td><OpPill tone={row.group}>{statusLabel(row.group)}</OpPill></td>
                <td><OpPill tone={(row.priority || 'Medium').toLowerCase() === 'high' ? 'high' : (row.priority || '').toLowerCase() === 'low' ? 'low' : 'medium'}>{row.priority || 'Medium'}</OpPill></td>
                <td>
                  <div className="op-row-actions">
                    <button type="button" title="Open packet" onClick={() => onOpen(row)}>↗</button>
                    {row.item && row.group === 'done' ? <button type="button" title="Follow up" onClick={() => onFollowUp(row.item)}>↻</button> : null}
                    {row.item ? <button type="button" title="Copy report" onClick={() => onCopy(row.item)}>⧉</button> : null}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="6" className="op-empty-row">No work in this lane.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function projectHealthFor(project, dossier, attention) {
  const goal = cleanText(project.goal);
  const nextAction = cleanText(project.nextAction);
  const registryGoalStatus = cleanText(project.goalStatus).toLowerCase();
  if (registryGoalStatus.includes('achieved')) return { tone: 'achieved', label: 'Goal achieved', detail: 'objective completed' };
  if (attention || !goal || !nextAction) return { tone: 'nogoal', label: 'No direction', detail: 'goal or next action missing' };
  if (dossier?.waiting?.length || (dossier?.active || []).some((item) => ['blocked', 'error'].includes(runnerStatus(item)))) {
    return { tone: 'blocked', label: 'Waiting approval', detail: 'decision or runner blocker' };
  }
  const active = dossier?.active || [];
  if (active.some((item) => runnerStatus(item) === 'claimed')) return { tone: 'working', label: 'Implementing', detail: 'runner has an active packet' };
  if (active.length) return { tone: 'ready', label: 'Exploring', detail: 'work packet queued' };
  if ((dossier?.projectItems || []).some((item) => runnerStatus(item) === 'needs-authority')) {
    return { tone: 'stuck', label: 'Needs safe authority', detail: 'packet is visible but not runnable' };
  }
  const latest = dossier?.completed?.[0];
  if (latest) {
    const report = handoffReportFor(latest);
    if ((report.goalStatus || '').toLowerCase().includes('achieved')) return { tone: 'achieved', label: 'Goal achieved', detail: 'objective completed' };
    if (report.nextAction) return { tone: 'recommendation', label: 'Recommendation ready', detail: 'CEO/specialist handoff exists' };
    return { tone: 'moving', label: 'Evidence captured', detail: 'completed packet exists' };
  }
  if (cleanText(project.progress)) return { tone: 'moving', label: 'Moving', detail: 'progress recorded' };
  return { tone: 'stuck', label: 'Stuck', detail: 'no progress recorded' };
}

function ProjectAnalyticsBadge({ analytics }) {
  if (!analytics) return <div className="op-analytics-badge muted">GA: no property</div>;
  if (!analytics.configured) return <div className="op-analytics-badge muted">GA not configured</div>;
  const metrics = analytics.metrics || {};
  const delta = analytics.delta || {};
  return (
    <div className={`op-analytics-badge ${Number(metrics.activeUsers || 0) > 0 ? 'active' : 'zero'}`}>
      <strong>{formatMetric(metrics.activeUsers)} users</strong>
      <span>{formatMetric(metrics.pageViews)} views · {formatPercent(delta.activeUsers)} users</span>
    </div>
  );
}

function OperatingProjectCard({ project, attention, dossier, analytics, onOpen, onAssign }) {
  const health = projectHealthFor(project, dossier, attention);
  return (
    <article className={`op-project ${health.tone}`}>
      <div className="op-project-top">
        <div>
          <h4>{project.name}</h4>
          {project.domain ? <span>{project.domain}</span> : <span>{project.status || 'development'}</span>}
        </div>
        <OpPill tone={(project.priority || 'Medium').toLowerCase() === 'high' ? 'high' : (project.priority || '').toLowerCase() === 'low' ? 'low' : 'medium'}>{project.priority || 'Medium'}</OpPill>
      </div>
      <ProjectAnalyticsBadge analytics={analytics} />
      <div className={`op-health ${health.tone}`}><span />{health.label} · {health.detail}</div>
      <p className="op-project-goal">{cleanText(project.goal) || 'No goal defined.'}</p>
      <div className="op-next"><strong>Next action</strong>{cleanText(project.nextAction) || 'No next action queued.'}</div>
      <div className="op-project-foot">
        <span><AgentAvatar name={project.ownerAgent || 'CEO Agent'} small />{project.ownerAgent || 'CEO Agent'}</span>
        <div>
          <button type="button" className="op-btn sm ghost" onClick={() => onAssign(project)}>Assign</button>
          <button type="button" className="op-btn sm" onClick={() => onOpen(project.id)}>Open</button>
        </div>
      </div>
    </article>
  );
}

function ExplorationThreadsBoard({ threads, onOpenItem, onOpenProject, onFollowUp }) {
  return (
    <section className="op-card op-thread-board" id="exploration-threads">
      <div className="op-section-header">
        <div>
          <span className="op-section-eyebrow">CEO exploration threads</span>
          <h3>Split → evidence → CEO synthesis</h3>
          <p>Threads group broad subjects so specialist branches roll back into one recommendation, approval, or follow-up.</p>
        </div>
        <OpPill tone="ready">{threads.length} visible</OpPill>
      </div>
      {threads.length ? (
        <div className="op-thread-grid">
          {threads.map((thread) => {
            const report = thread.report;
            const latest = thread.latestItem;
            const pullRequestAction = projectPullRequestAction(thread.project);
            return (
              <article className={`op-thread-card ${thread.status.tone}`} key={thread.id}>
                <div className="op-thread-top">
                  <div>
                    <h4>{thread.title}</h4>
                    <span>{thread.project?.domain || thread.projectId || 'general'}</span>
                  </div>
                  <OpPill tone={thread.status.tone}>{thread.status.label}</OpPill>
                </div>
                <div className="op-thread-flow">
                  <span>CEO</span>
                  <b>→</b>
                  <span>{thread.branchCount || 1} branch{thread.branchCount === 1 ? '' : 'es'}</span>
                  <b>→</b>
                  <span>CEO synthesis</span>
                </div>
                <div className="op-thread-branches">
                  {(thread.specialistAgents.length ? thread.specialistAgents : thread.agents).slice(0, 5).map((agent) => (
                    <OpPill tone={agent === 'CEO Agent' ? 'muted' : 'ready'} key={agent}>{agent.replace(' Agent', '')}</OpPill>
                  ))}
                </div>
                <p>{shortText(report?.summary || report?.nextAction || latest?.workingOn || latest?.question || 'No synthesis yet.', 210)}</p>
                {report?.nextAction ? <div className="op-thread-next"><strong>Recommended next</strong>{shortText(report.nextAction, 180)}</div> : null}
                {thread.analytics ? <ProjectAnalyticsBadge analytics={thread.analytics} /> : null}
                <div className="op-thread-foot">
                  <span>{thread.completed.length} done · {thread.items.filter(isActiveWork).length} active · {thread.waiting.length} waiting</span>
                  <div>
                    {pullRequestAction ? (
                      <a className="op-btn sm" href={pullRequestAction.url} target="_blank" rel="noopener noreferrer">
                        {pullRequestAction.label}
                      </a>
                    ) : null}
                    {thread.project ? <button type="button" className="op-btn sm ghost" onClick={() => onOpenProject(thread.project.id)}>Project</button> : null}
                    {latest ? <button type="button" className="op-btn sm" onClick={() => onOpenItem(latest.kind === 'question'
                      ? { question: latest, kind: 'question', group: 'needsyou' }
                      : { item: latest, group: runnerStatus(latest), kind: 'work' })}>Open</button> : null}
                    {thread.latestCompleted ? <button type="button" className="op-btn sm ghost" onClick={() => onFollowUp(thread.latestCompleted)}>Follow up</button> : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="op-empty-row">No exploration threads yet. Start a project packet and the CEO split/synthesis will appear here.</div>
      )}
    </section>
  );
}

function agentReturnTarget(agent) {
  if (agent.name === 'CEO Agent') return 'Z or specialist branch';
  if (agent.name === 'QA Release Agent') return 'CTO / CEO';
  if (agent.name === 'Research Agent' || agent.name === 'Data Agent' || agent.name === 'Product Agent') return 'CEO synthesis';
  return agent.delegatedTo?.includes('CEO Agent') ? 'CEO Agent' : 'CEO Agent / owner';
}

function AgentLoadBoard({ agents, activeWorkItems, pendingQuestions }) {
  const activeByAgent = new Map();
  activeWorkItems.forEach((item) => {
    activeByAgent.set(item.agent, [...(activeByAgent.get(item.agent) || []), item]);
  });
  const waitingByAgent = new Map();
  pendingQuestions.forEach((item) => {
    const key = item.owner || item.agent || 'Agent';
    waitingByAgent.set(key, [...(waitingByAgent.get(key) || []), item]);
  });
  const names = new Set(agents.map((agent) => agent.name));

  return (
    <section className="op-card op-agent-load" id="agents">
      <div className="op-section-header">
        <div>
          <span className="op-section-eyebrow">Agent roster, prompts & routes</span>
          <h3>{agents.length} agents visible</h3>
          <p>Each card shows how the agent takes over, who it can route to, when it asks Z, and where it returns.</p>
        </div>
      </div>
      <div className="op-agent-load-grid">
        {agents.map((agent) => {
          const active = activeByAgent.get(agent.name) || [];
          const waiting = waitingByAgent.get(agent.name) || [];
          const blocked = active.filter((item) => ['blocked', 'error'].includes(runnerStatus(item))).length + waiting.length;
          const load = Math.min(100, (active.length * 28) + (blocked * 24) + (CORE_AGENT_NAMES.has(agent.name) ? 8 : 0));
          const unknownTargets = (agent.delegatedTo || []).filter((target) => !names.has(target));
          return (
            <article className="op-agent-load-card" key={agent.name}>
              <AgentAvatar name={agent.name} />
              <div>
                <strong>{agent.name}</strong>
                <span>{agent.role}</span>
                <div className="op-agent-load-tags">
                  {blocked ? <OpPill tone="needsyou">{blocked} waiting</OpPill> : active.length ? <OpPill tone="working">{active.length} active</OpPill> : <OpPill tone="done">free</OpPill>}
                  {unknownTargets.length ? <OpPill tone="blocked">bad route: {unknownTargets.join(', ')}</OpPill> : null}
                </div>
                <div className="op-agent-prompt">
                  <p><strong>Takes over:</strong> {shortText(agent.workingOn, 150)}</p>
                  <p><strong>Can coordinate with:</strong> {(agent.delegatedTo || []).join(', ') || 'No configured specialists'}</p>
                  <p><strong>Returns to:</strong> {agentReturnTarget(agent)}</p>
                  <p><strong>Ask Z when:</strong> {shortText(agent.waitingOn || agent.guardrails, 150)}</p>
                </div>
                <div className="op-loadbar"><span style={{ width: `${load}%` }} /></div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RunnerHealthStrip({ status, state }) {
  const record = runnerRecordFor(status);
  const health = runnerHealthLabel(status);
  const metric = (label, value, title) => <div><dt>{label}</dt><dd title={title}>{value}</dd></div>;

  return (
    <section className="runner-health-strip op-card" aria-label="Runner health">
      <div className="runner-health-heading">
        <div>
          <span className="op-section-eyebrow">Runner health</span>
          <h3>Execution visibility</h3>
        </div>
        <span className={`runner-health-state ${health.tone}`} role="status">
          {state === 'unavailable' ? 'Status unavailable' : health.label}
        </span>
      </div>
      <dl className="runner-health-grid">
        {metric('Queue', formatRunnerCount(status?.queueDepth))}
        {metric('Backed off / dead-letter', `${formatRunnerCount(status?.backedOffCount)} / ${formatRunnerCount(status?.deadLetterCount)}`)}
        {metric('Oldest queued', formatRunnerDuration(status?.oldestQueuedAgeMs))}
        {metric('Latest poll', formatRunnerAge(record?.lastPollAt), safeTimestampTitle(record?.lastPollAt))}
        {metric('Latest heartbeat', formatRunnerAge(record?.lastHeartbeatAt), safeTimestampTitle(record?.lastHeartbeatAt))}
        {metric('Latest completion', formatRunnerAge(record?.lastCompleteAt), safeTimestampTitle(record?.lastCompleteAt))}
        {metric('Active log', safeRunnerText(record?.activeLogId) || 'None')}
        {metric('Selected model', formatRunnerModel(record?.selectedModel) || 'Not selected')}
        {metric('Reasoning effort', safeRunnerText(record?.reasoningEffort) || 'Not selected')}
      </dl>
      <p className="runner-health-note">Sensitive actions remain approval-gated.</p>
    </section>
  );
}

function AgentWorkspace() {
  const [board, setBoard] = useState(loadCachedState);
  const [draftAnswers, setDraftAnswers] = useState(loadDrafts);
  const [manualTask, setManualTask] = useState({
    agent: 'Hermes Agent',
    project: '',
    title: '',
    task: '',
    context: '',
    priority: 'Medium',
    supportingAgents: [],
    expectedResult: '',
    definitionOfDone: '',
    handoffTo: '',
    routingReason: '',
    parentLogId: '',
    rootLogId: '',
    rootThreadId: '',
    nextAction: '',
    stage: '',
    outcomeType: '',
    goalStatus: '',
  });
  const [loadState, setLoadState] = useState('loading');
  const [returningId, setReturningId] = useState(null);
  const [assigningTask, setAssigningTask] = useState(false);
  const [, setSavingProjectId] = useState(null);
  const manualTaskFormRef = useRef(null);
  const [newProject, setNewProject] = useState({
    name: '',
    domain: '',
    goal: '',
    priority: 'Medium',
    status: 'development',
  });
  const [addingProject, setAddingProject] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [workFilter, setWorkFilter] = useState('all');
  const [selectedKanban, setSelectedKanban] = useState(null);
  const [message, setMessage] = useState('');
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [analyticsState, setAnalyticsState] = useState('idle');
  const [runnerHealth, setRunnerHealth] = useState(null);
  const [runnerHealthState, setRunnerHealthState] = useState('loading');

  useEffect(() => {
    let mounted = true;

    async function loadBoard() {
      try {
        const response = await agentsAPI.getState();
        if (!mounted) return;
        const next = normalizeBoard(response.data);
        setBoard(next);
        cacheState(next);
        setLoadState('backend');
        setMessage('Loaded from backend. Answered items are relayed into agent logs; only new questions stay in the bucket.');
      } catch (error) {
        if (!mounted) return;
        const cached = loadCachedState();
        setBoard(cached);
        setLoadState('local');
        setMessage('Backend unavailable; using this browser cache until the API is reachable.');
      }
    }

    loadBoard();
    // Poll so runner claims/completions/blockers appear without a manual
    // remount; drafts live in separate state, so refreshes never clobber
    // an answer Z is typing.
    const timer = setInterval(loadBoard, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let timer = null;
    let controller = null;
    let requestSequence = 0;

    async function loadRunnerHealth() {
      const sequence = ++requestSequence;
      controller = new AbortController();
      try {
        const response = await agentsAPI.getRunnerStatus({ signal: controller.signal });
        if (!mounted || sequence !== requestSequence) return;
        setRunnerHealth(response.data);
        setRunnerHealthState('ready');
      } catch (error) {
        const canceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
        if (!mounted || sequence !== requestSequence || canceled) return;
        setRunnerHealthState('unavailable');
      } finally {
        if (mounted && sequence === requestSequence) {
          timer = setTimeout(loadRunnerHealth, 30000);
        }
      }
    }

    loadRunnerHealth();
    return () => {
      mounted = false;
      requestSequence += 1;
      clearTimeout(timer);
      controller?.abort();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadAnalytics() {
      setAnalyticsState('loading');
      try {
        const response = await analyticsAPI.getOverview('7d');
        if (!mounted) return;
        setAnalyticsOverview(response.data);
        setAnalyticsState('ready');
      } catch (error) {
        if (!mounted) return;
        setAnalyticsOverview(null);
        setAnalyticsState('unavailable');
      }
    }
    loadAnalytics();
    return () => { mounted = false; };
  }, []);

  const pendingQuestions = useMemo(
    () => (Array.isArray(board.pendingQuestions) ? board.pendingQuestions : buildLocalAgentWorkflow(board).pendingQuestions),
    [board]
  );

  const agentLog = useMemo(
    () => (Array.isArray(board.agentLog) ? board.agentLog : buildLocalAgentWorkflow(board).agentLog),
    [board]
  );

  const projects = useMemo(
    () => (Array.isArray(board.projects) ? board.projects : []),
    [board.projects]
  );

  const analyticsByDomain = useMemo(() => {
    const map = new Map();
    (analyticsOverview?.reports || analyticsOverview?.properties || []).forEach((item) => {
      const key = normalizeDomain(item.domain);
      if (key) map.set(key, item);
    });
    return map;
  }, [analyticsOverview]);

  const projectsNeedingAttention = useMemo(
    () => (Array.isArray(board.projectsNeedingAttention) ? board.projectsNeedingAttention : []),
    [board.projectsNeedingAttention]
  );

  const attentionProjectIds = useMemo(
    () => new Set(projectsNeedingAttention.map((item) => item.id)),
    [projectsNeedingAttention]
  );

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const attention = (attentionProjectIds.has(b.id) ? 1 : 0) - (attentionProjectIds.has(a.id) ? 1 : 0);
      if (attention !== 0) return attention;
      return (PROJECT_PRIORITY_ORDER[a.priority] ?? 1) - (PROJECT_PRIORITY_ORDER[b.priority] ?? 1);
    });
  }, [projects, attentionProjectIds]);

  const filteredProjects = useMemo(() => {
    const query = cleanText(projectSearch).toLowerCase();
    return sortedProjects.filter((project) => {
      const matchesQuery = !query || [project.name, project.domain, project.goal, project.nextAction, project.ownerAgent]
        .some((value) => cleanText(value).toLowerCase().includes(query));
      const matchesFilter = projectFilter === 'all'
        || (projectFilter === 'needs-attention' && attentionProjectIds.has(project.id))
        || (projectFilter === 'high' && project.priority === 'High')
        || project.status === projectFilter
        || project.ownerAgent === projectFilter;
      return matchesQuery && matchesFilter;
    });
  }, [sortedProjects, projectSearch, projectFilter, attentionProjectIds]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  const selectedProjectDossier = useMemo(
    () => buildProjectDossier(selectedProject, agentLog, pendingQuestions),
    [selectedProject, agentLog, pendingQuestions]
  );

  const coreAgents = useMemo(
    () => board.agents.filter((agent) => CORE_AGENT_NAMES.has(agent.name)),
    [board.agents]
  );

  const specialistAgents = useMemo(
    () => board.agents.filter((agent) => !CORE_AGENT_NAMES.has(agent.name)),
    [board.agents]
  );

  const knownAgentNames = useMemo(
    () => new Set(board.agents.map((agent) => agent.name)),
    [board.agents]
  );

  const resolveKnownAgentName = (...candidates) => {
    for (const candidate of candidates) {
      const parts = asList(candidate)
        .flatMap((value) => value.split(/[,→]/).map(cleanText))
        .filter(Boolean);
      for (const part of parts) {
        if (knownAgentNames.has(part)) return part;
      }
    }
    return knownAgentNames.has('CEO Agent') ? 'CEO Agent' : (board.agents[0]?.name || 'CEO Agent');
  };

  const pendingByParentLog = useMemo(() => {
    const map = new Map();
    pendingQuestions.forEach((question) => {
      if (question.parentLogId) {
        map.set(question.parentLogId, question);
      }
    });
    return map;
  }, [pendingQuestions]);

  const activeWorkItems = useMemo(
    () => agentLog.filter(isActiveWork),
    [agentLog]
  );

  const recentCompletedItems = useMemo(
    () => agentLog
      .filter((item) => runnerStatus(item) === 'completed')
      .sort((a, b) =>
        (Date.parse(b.runner?.finishedAt || b.runner?.updatedAt || 0) || 0)
        - (Date.parse(a.runner?.finishedAt || a.runner?.updatedAt || 0) || 0))
      .slice(0, 5),
    [agentLog]
  );

  const activeAgentLanes = useMemo(() => {
    const activeAgents = board.agents
      .map((agent) => ({
        agent,
        items: activeWorkItems.filter((item) => item.agent === agent.name),
      }))
      .filter((lane) => lane.items.length > 0 || CORE_AGENT_NAMES.has(lane.agent.name));
    return activeAgents;
  }, [board.agents, activeWorkItems]);

  const flowMetrics = useMemo(() => ({
    active: activeWorkItems.length,
    claimed: activeWorkItems.filter((item) => runnerStatus(item) === 'claimed').length,
    blocked: activeWorkItems.filter((item) => runnerStatus(item) === 'blocked').length + pendingQuestions.length,
    completed: recentCompletedItems.length,
  }), [activeWorkItems, pendingQuestions.length, recentCompletedItems.length]);

  // eslint-disable-next-line no-unused-vars
  const kanbanColumns = useMemo(() => [
    {
      key: 'waiting',
      title: 'Waiting for Z',
      items: pendingQuestions,
      emptyText: 'No decisions needed',
    },
    {
      key: 'ready',
      title: 'Ready',
      items: activeWorkItems.filter((item) => runnerStatus(item) === 'queued'),
      emptyText: 'Nothing queued',
    },
    {
      key: 'working',
      title: 'Working',
      items: activeWorkItems.filter((item) => runnerStatus(item) === 'claimed'),
      emptyText: 'Runner idle',
    },
    {
      key: 'blocked',
      title: 'Blocked',
      items: activeWorkItems.filter((item) => ['blocked', 'error'].includes(runnerStatus(item))),
      emptyText: 'No blocked work',
    },
    {
      key: 'done',
      title: 'Done',
      items: recentCompletedItems,
      emptyText: 'No recent completions',
    },
  ], [activeWorkItems, pendingQuestions, recentCompletedItems]);

  const selectedKanbanItem = useMemo(() => {
    if (!selectedKanban) return null;
    if (selectedKanban.columnKey === 'waiting') return pendingQuestions.find((item) => item.id === selectedKanban.id);
    return agentLog.find((item) => item.id === selectedKanban.id);
  }, [selectedKanban, pendingQuestions, agentLog]);

  const nowRecommendation = useMemo(() => {
    const blocked = activeWorkItems.find((item) => ['blocked', 'error'].includes(runnerStatus(item)));
    const working = activeWorkItems.find((item) => runnerStatus(item) === 'claimed');
    const ready = activeWorkItems.find((item) => runnerStatus(item) === 'queued');
    const attention = projectsNeedingAttention[0];
    if (pendingQuestions[0]) {
      return { kind: 'waiting', item: pendingQuestions[0], title: 'Answer one blocker', detail: `${pendingQuestions[0].owner} needs: ${pendingQuestions[0].question}`, actionLabel: 'Answer now' };
    }
    if (blocked) return { kind: 'blocked', item: blocked, title: 'Unblock runner work', detail: `${blocked.agent} is blocked on ${blocked.question}`, actionLabel: 'Open blocker' };
    if (working) return { kind: 'working', item: working, title: 'Runner is working', detail: `${working.agent} owns ${working.question}`, actionLabel: 'Open work packet' };
    if (ready) return { kind: 'ready', item: ready, title: 'Work is ready', detail: `${ready.agent} can claim ${ready.question}`, actionLabel: 'Open ready packet' };
    if (attention) return { kind: 'project', item: attention, title: 'Define next project move', detail: `${attention.name} needs ${attention.reasons.join(' and ')}.`, actionLabel: 'Open project' };
    if (recentCompletedItems[0]) return { kind: 'done', item: recentCompletedItems[0], title: 'Review latest result', detail: shortText(resultSummaryFor(recentCompletedItems[0]) || recentCompletedItems[0].question, 180), actionLabel: 'Open report' };
    return { kind: 'clear', item: null, title: 'No active agent blocker', detail: 'The board is clear. Start a task or set a project next action when ready.', actionLabel: 'Start task' };
  }, [pendingQuestions, activeWorkItems, projectsNeedingAttention, recentCompletedItems]);

  const liveHandoffs = useMemo(() => agentLog
    .filter((item) => handoffReportFor(item).createdTaskIds.length)
    .slice(-12)
    .reverse(), [agentLog]);

  const projectDossiers = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      map.set(project.id, buildProjectDossier(project, agentLog, pendingQuestions));
    });
    return map;
  }, [projects, agentLog, pendingQuestions]);

  const explorationThreads = useMemo(
    () => buildExplorationThreads(agentLog, pendingQuestions, projects, analyticsByDomain),
    [agentLog, pendingQuestions, projects, analyticsByDomain]
  );

  const operatingWorkRows = useMemo(() => {
    const questionRows = pendingQuestions.map((item) => ({
      id: `question:${item.id}`,
      kind: 'question',
      question: item,
      agent: item.owner || item.agent || 'Agent',
      role: 'Waiting for Z',
      title: item.question,
      from: item.from || 'Z decision',
      to: item.owner || item.agent,
      project: item.project,
      group: 'needsyou',
      priority: item.priority || 'High',
      summary: item.recommendation || item.why || '',
      threadLabel: item.projectName || item.project || 'Decision thread',
      stage: 'Waiting approval',
    }));

    const activeRows = activeWorkItems.map((item) => {
      const report = handoffReportFor(item);
      return {
        id: `work:${item.id}`,
        kind: 'work',
        item,
        agent: item.agent || 'Agent',
        role: contextSourceLabel(item),
        title: item.question || item.title || item.id,
        from: item.from || contextSourceLabel(item),
        to: '',
        project: item.project || item.projectName,
        group: statusGroupForItem(item),
        priority: item.priority || 'Medium',
        summary: report.summary || report.nextAction || item.nextAction || item.workingOn || item.message,
        threadLabel: item.projectName || item.project || item.rootThreadId || 'General thread',
        stage: displayStageForItem(item),
      };
    });

    const completedRows = recentCompletedItems.map((item) => {
      const report = handoffReportFor(item);
      return {
        id: `done:${item.id}`,
        kind: 'done',
        item,
        agent: item.agent || 'Agent',
        role: report.createdTaskIds.length ? 'Completed + linked follow-up' : 'Completed result',
        title: item.question || item.title || item.id,
        from: item.from || contextSourceLabel(item),
        to: report.handoffTo,
        project: item.project || item.projectName,
        group: 'done',
        priority: item.priority || 'Medium',
        summary: report.summary || report.nextAction,
        threadLabel: item.projectName || item.project || item.rootThreadId || 'General thread',
        stage: displayStageForItem(item),
      };
    });

    return [...questionRows, ...activeRows, ...completedRows];
  }, [pendingQuestions, activeWorkItems, recentCompletedItems]);

  const operatingKpis = useMemo(() => ({
    needsYou: pendingQuestions.length,
    inFlight: activeWorkItems.length,
    attention: projects.filter((project) => {
      const dossier = projectDossiers.get(project.id);
      return ['nogoal', 'stuck', 'blocked'].includes(projectHealthFor(project, dossier, attentionProjectIds.has(project.id)).tone);
    }).length,
    done: recentCompletedItems.length,
  }), [pendingQuestions.length, activeWorkItems.length, projects, projectDossiers, attentionProjectIds, recentCompletedItems.length]);

  const openOperatingRow = (row) => {
    if (row.kind === 'question') {
      return scrollToBoardItem('question', row.question.id);
    }
    if (row.item) {
      const columnKey = row.group === 'done' ? 'done' : runnerStatus(row.item);
      setSelectedKanban({ columnKey, id: row.item.id });
      window.setTimeout(() => {
        const panel = document.getElementById('selected-handoff-report');
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  };

  const prefillProjectTask = (project) => {
    setManualTask({
      agent: project.ownerAgent || 'CEO Agent',
      project: project.id,
      title: `Advance ${project.name}`,
      task: cleanText(project.nextAction) || `Inspect ${project.name} and return the smallest useful next action.`,
      context: `Goal: ${cleanText(project.goal) || 'No goal defined yet.'}\nProgress: ${cleanText(project.progress) || 'No progress recorded.'}`,
      priority: project.priority || 'Medium',
      supportingAgents: [],
      expectedResult: 'Status / What matters / Evidence / Blocked by / Next action.',
      definitionOfDone: 'Return a CEO-readable handoff and ask Z only if blocked by approval or missing access.',
      handoffTo: project.ownerAgent || 'CEO Agent',
      routingReason: `Manual steering from project card for ${project.name}`,
    });
    setMessage(`Prefilled agent task for ${project.name}. Review and start it.`);
    manualTaskFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const manualTaskReady = Boolean(cleanText(manualTask.agent) && cleanText(manualTask.task));
  const manualTaskBlocker = !cleanText(manualTask.agent)
    ? 'Choose an agent first.'
    : !cleanText(manualTask.task)
      ? 'Describe the task first, or use Follow up from a recommendation to prefill it.'
      : '';

  const updateManualTask = (field, value) => {
    setManualTask((current) => ({ ...current, [field]: value }));
  };

  const toggleSupportingAgent = (agentName) => {
    setManualTask((current) => {
      const currentAgents = new Set(current.supportingAgents || []);
      if (currentAgents.has(agentName)) currentAgents.delete(agentName);
      else currentAgents.add(agentName);
      return { ...current, supportingAgents: Array.from(currentAgents) };
    });
  };

  const scrollToBoardItem = (prefix, id) => {
    window.setTimeout(() => {
      const node = document.getElementById(safeDomId(prefix, id));
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget = node.querySelector('textarea, button, input, select');
        focusTarget?.focus?.();
      }
    }, 50);
  };

  // eslint-disable-next-line no-unused-vars
  const selectKanbanItem = (column, item) => {
    setSelectedKanban({ columnKey: column.key, id: item.id });
    if (column.key === 'waiting') scrollToBoardItem('question', item.id);
    else scrollToBoardItem('work', item.id);
  };

  const copyHandoffReport = async (item) => {
    try {
      await navigator.clipboard.writeText(formatHandoffReport(item));
      setMessage('Copied handoff report.');
    } catch (error) {
      setMessage('Could not copy report automatically; browser clipboard blocked it.');
    }
  };

  const copyProjectReport = async (project, dossier) => {
    try {
      await navigator.clipboard.writeText(formatProjectReport(project, dossier));
      setMessage(`Copied ${project.name} project report.`);
    } catch (error) {
      setMessage('Could not copy project report automatically; browser clipboard blocked it.');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleNowAction = () => {
    const { item, kind } = nowRecommendation;
    if (kind === 'waiting' && item) return scrollToBoardItem('question', item.id);
    if (['blocked', 'working', 'ready', 'done'].includes(kind) && item) {
      setSelectedKanban({ columnKey: kind === 'done' ? 'done' : runnerStatus(item), id: item.id });
      return scrollToBoardItem('work', item.id);
    }
    if (kind === 'project' && item) {
      setSelectedProjectId(item.id);
      return window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    manualTaskFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const useLatestNextAction = async (project, item) => {
    const nextAction = handoffReportFor(item).nextAction;
    if (!nextAction) return;
    await saveProject({ id: project.id, name: project.name, nextAction });
  };

  // Turns a completed packet's result into the next delegated task: prefills
  // the manual-task form and scrolls it into view. Submission stays the
  // normal startManualTask flow.
  const startFollowUp = (item) => {
    const report = handoffReportFor(item);
    const recommendedTask = cleanText(report.nextAction)
      || cleanText(item.nextAction)
      || `Continue from this completed handoff: ${cleanText(item.question || item.title || item.id)}`;
    const context = [
      resultSummaryFor(item) || item.workingOn || '',
      report.evidence?.length ? `Evidence: ${report.evidence.join(' | ')}` : '',
      report.blockedBy ? `Blocked by: ${report.blockedBy}` : '',
    ].filter(Boolean).join('\n\n');

    const followUpAgent = resolveKnownAgentName(report.handoffTo, item.handoffTo, item.agent, 'CEO Agent');
    const handoffTarget = report.handoffTo || item.handoffTo || item.agent || followUpAgent;

    setManualTask({
      agent: followUpAgent,
      project: item.project || '',
      title: `Follow up: ${shortText(item.question || item.title || item.id, 60)}`,
      task: recommendedTask,
      context,
      priority: item.priority || 'Medium',
      supportingAgents: item.delegatedTo || [],
      expectedResult: 'Status / What matters / Evidence / Blocked by / Next action.',
      definitionOfDone: 'Create a runner handoff with a concrete outcome and ask Z only if blocked by approval or missing access.',
      handoffTo: handoffTarget,
      routingReason: `Follow-up from ${item.id}`,
      parentLogId: item.id || '',
      rootLogId: item.rootLogId || item.id || '',
      rootThreadId: item.rootThreadId || threadKeyForItem(item),
      nextAction: recommendedTask,
      stage: 'follow-up',
      outcomeType: 'continuation',
      goalStatus: 'in-progress',
    });
    setMessage(`Follow-up is prefilled for ${followUpAgent} — review it, then Start agent task.`);
    manualTaskFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const updateDraft = (id, value) => {
    setDraftAnswers((current) => {
      const next = { ...current, [id]: value };
      cacheDrafts(next);
      return next;
    });
  };

  // Sends only the edited/new project; the backend merges per-item by id, so
  // this can never clobber other projects or runner-written updates.
  const saveProject = async (projectUpdate) => {
    setSavingProjectId(projectUpdate.id || 'new');
    setMessage(`Saving project ${projectUpdate.name}...`);
    try {
      const response = await agentsAPI.updateState({ projects: [projectUpdate] });
      const nextBoard = normalizeBoard(response.data);
      setBoard(nextBoard);
      cacheState(nextBoard);
      setLoadState('backend');
      setMessage(`Project ${projectUpdate.name} saved.`);
      return true;
    } catch (error) {
      setMessage('Could not save the project. Your edits stayed in the form; retry when the API is reachable.');
      return false;
    } finally {
      setSavingProjectId(null);
    }
  };

  const updateNewProject = (field, value) => {
    setNewProject((current) => ({ ...current, [field]: value }));
  };

  const addProject = async (event) => {
    event.preventDefault();
    if (!cleanText(newProject.name)) {
      setMessage('Give the new project a name first.');
      return;
    }
    setAddingProject(true);
    const ok = await saveProject({ ...newProject });
    setAddingProject(false);
    if (ok) {
      setNewProject({ name: '', domain: '', goal: '', priority: 'Medium', status: 'development' });
    }
  };

  const returnToAgent = async (item, answerOverride = '') => {
    const answer = cleanText(answerOverride || draftAnswers[item.id]);
    if (!answer) {
      setMessage('Type an answer before returning this question to the agent.');
      return;
    }

    setReturningId(item.id);
    setMessage(`Returning answer to ${item.owner}...`);

    try {
      // Send only the new answer. The backend merges answers per-key and
      // keeps its own agents/delegations/questions, so a stale board here
      // can't overwrite state the runner changed since we loaded.
      const response = await agentsAPI.updateState({
        answers: { [item.id]: answer },
      });
      const nextBoard = normalizeBoard(response.data);
      const nextDrafts = { ...draftAnswers };
      delete nextDrafts[item.id];

      setBoard(nextBoard);
      setDraftAnswers(nextDrafts);
      cacheState(nextBoard);
      cacheDrafts(nextDrafts);
      setLoadState('backend');
      setMessage(`Returned to ${item.owner}. The agent log now owns this answer.`);
    } catch (error) {
      cacheDrafts(draftAnswers);
      setLoadState('local');
      setMessage('Could not return to agent. Draft stayed in this window; retry when the API is reachable.');
    } finally {
      setReturningId(null);
    }
  };

  const startManualTask = async (event) => {
    event.preventDefault();
    if (!manualTaskReady) {
      setMessage('Choose an agent and describe the task before starting it.');
      return;
    }

    setAssigningTask(true);
    setMessage(`Assigning task to ${manualTask.agent}...`);

    try {
      const response = await agentsAPI.createTask({
        agent: manualTask.agent,
        title: manualTask.title,
        task: manualTask.task,
        context: manualTask.context,
        priority: manualTask.priority,
        supportingAgents: manualTask.supportingAgents,
        expectedResult: manualTask.expectedResult,
        definitionOfDone: manualTask.definitionOfDone,
        handoffTo: manualTask.handoffTo,
        routingReason: manualTask.routingReason,
        parentLogId: manualTask.parentLogId,
        rootLogId: manualTask.rootLogId,
        rootThreadId: manualTask.rootThreadId,
        nextAction: manualTask.nextAction || manualTask.task,
        stage: manualTask.stage,
        outcomeType: manualTask.outcomeType,
        goalStatus: manualTask.goalStatus,
        ...(manualTask.project ? { project: manualTask.project } : {}),
      });
      const nextBoard = normalizeBoard(response.data.state || response.data);
      setBoard(nextBoard);
      cacheState(nextBoard);
      setLoadState('backend');
      setManualTask({
        agent: manualTask.agent,
        project: '',
        title: '',
        task: '',
        context: '',
        priority: 'Medium',
        supportingAgents: [],
        expectedResult: '',
        definitionOfDone: '',
        handoffTo: '',
        routingReason: '',
        parentLogId: '',
        rootLogId: '',
        rootThreadId: '',
        nextAction: '',
        stage: '',
        outcomeType: '',
        goalStatus: '',
      });
      setMessage(`Started ${manualTask.agent} task. It is now in the agent log for the runner to claim.`);
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.message || error.message;
      setMessage(detail
        ? `Could not start the manual task: ${detail}`
        : 'Could not start the manual task. Check login/API and try again.');
    } finally {
      setAssigningTask(false);
    }
  };

  if (selectedProject && selectedProjectDossier) {
    return (
      <ProjectDetailPage
        project={selectedProject}
        dossier={selectedProjectDossier}
        onBack={() => setSelectedProjectId(null)}
        onFollowUp={(item) => {
          startFollowUp(item);
          setSelectedProjectId(null);
        }}
        onCopyReport={copyHandoffReport}
        onCopyProjectReport={copyProjectReport}
        onUseNextAction={useLatestNextAction}
      />
    );
  }

  return (
    <div className="agent-workspace">
      <section className="op-board-hero">
        <div className="op-hero-copy">
          <span className="op-section-eyebrow">Fable-style cockpit</span>
          <h2>Agent Operating Board</h2>
          <p>One screen to see what needs you, who is doing what, where every project stands, and how to steer it.</p>
          <p className={`agent-sync-status ${loadState}`}>{message || 'Loading agent board...'} {analyticsState === 'ready' ? 'GA evidence loaded.' : analyticsState === 'unavailable' ? 'GA evidence unavailable.' : ''}</p>
        </div>
        <div className="op-hero-actions">
          <button type="button" className="op-command" onClick={() => manualTaskFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
            <span>⌘</span> Steer anything… <kbd>Assign work</kbd>
          </button>
          <button type="button" className="op-btn primary" onClick={() => manualTaskFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>＋ Assign work</button>
        </div>
      </section>

      <RunnerHealthStrip status={runnerHealth} state={runnerHealthState} />

      <div className="op-kpi-grid">
        <OperatingKpi tone="needsyou" number={operatingKpis.needsYou} label="Need your decision" onClick={() => scrollToBoardItem('question', pendingQuestions[0]?.id)} />
        <OperatingKpi tone="working" number={operatingKpis.inFlight} label="In flight now" onClick={() => document.getElementById('in-flight')?.scrollIntoView({ behavior: 'smooth' })} />
        <OperatingKpi tone="blocked" number={operatingKpis.attention} label="Projects needing attention" onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })} />
        <OperatingKpi tone="done" number={operatingKpis.done} label="Done recently" onClick={() => setWorkFilter('done')} />
      </div>

      <NeedsYouBoard
        questions={pendingQuestions}
        draftAnswers={draftAnswers}
        updateDraft={updateDraft}
        onReturn={returnToAgent}
        returningId={returningId}
      />

      <ExplorationThreadsBoard
        threads={explorationThreads}
        onOpenItem={openOperatingRow}
        onOpenProject={(projectId) => {
          setSelectedProjectId(projectId);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onFollowUp={startFollowUp}
      />

      <OperatingWorkTable
        rows={operatingWorkRows}
        filter={workFilter}
        onFilter={setWorkFilter}
        onOpen={openOperatingRow}
        onFollowUp={startFollowUp}
        onCopy={copyHandoffReport}
      />

      {selectedKanbanItem && selectedKanban?.columnKey !== 'waiting' ? (
        <div className="selected-handoff-panel op-selected-report" id="selected-handoff-report">
          <HandoffReportCard item={selectedKanbanItem} onCopy={copyHandoffReport} />
        </div>
      ) : null}

      <section className="component-container manual-agent-task">
        <div className="component-header">
          <div>
            <h2>Start agent task</h2>
            <p className="component-subtitle">
              Manually assign work to an agent. This creates an agent-log item; the runner claims it on the next tick.
            </p>
          </div>
        </div>

        <form className="manual-task-form" onSubmit={startManualTask} ref={manualTaskFormRef}>
          <label>
            Agent
            <select
              value={manualTask.agent}
              onChange={(event) => updateManualTask('agent', event.target.value)}
            >
              {board.agents.map((agent) => (
                <option value={agent.name} key={agent.name}>{agent.name}</option>
              ))}
            </select>
          </label>

          <label>
            Project
            <select
              value={manualTask.project}
              onChange={(event) => updateManualTask('project', event.target.value)}
            >
              <option value="">— none —</option>
              {projects.map((project) => (
                <option value={project.id} key={project.id}>{project.name}</option>
              ))}
            </select>
          </label>

          <label>
            Short title
            <input
              value={manualTask.title}
              onChange={(event) => updateManualTask('title', event.target.value)}
              placeholder="Example: Check Dokploy volume backup"
            />
          </label>

          <label className="manual-task-full">
            Task
            <textarea
              value={manualTask.task}
              onChange={(event) => updateManualTask('task', event.target.value)}
              placeholder="Tell the agent exactly what to do. It will ask a Waiting for Z question if blocked."
              rows={3}
            />
          </label>

          <label className="manual-task-full">
            Extra context / constraints
            <textarea
              value={manualTask.context}
              onChange={(event) => updateManualTask('context', event.target.value)}
              placeholder="Optional: repo, URL, safety limit, expected output. Do not paste raw secrets."
              rows={2}
            />
          </label>

          <label>
            Priority
            <select
              value={manualTask.priority}
              onChange={(event) => updateManualTask('priority', event.target.value)}
            >
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </label>

          <label className="manual-task-full">
            Expected result
            <input
              value={manualTask.expectedResult}
              onChange={(event) => updateManualTask('expectedResult', event.target.value)}
              placeholder="What should the handoff report prove?"
            />
          </label>

          <label className="manual-task-full">
            Definition of done
            <textarea
              value={manualTask.definitionOfDone}
              onChange={(event) => updateManualTask('definitionOfDone', event.target.value)}
              placeholder="Example: include Status / What matters / Evidence / Blocked by / Next action."
              rows={2}
            />
          </label>

          <label>
            Handoff target
            <input
              value={manualTask.handoffTo}
              onChange={(event) => updateManualTask('handoffTo', event.target.value)}
              placeholder="Optional agent/person"
            />
          </label>

          <label>
            Routing reason
            <input
              value={manualTask.routingReason}
              onChange={(event) => updateManualTask('routingReason', event.target.value)}
              placeholder="Why this agent?"
            />
          </label>

          <div className="manual-task-full supporting-agent-picker">
            <strong>Supporting agents</strong>
            <div>
              {board.agents
                .filter((agent) => agent.name !== manualTask.agent)
                .slice(0, 8)
                .map((agent) => (
                  <button
                    type="button"
                    className={manualTask.supportingAgents?.includes(agent.name) ? 'selected' : ''}
                    onClick={() => toggleSupportingAgent(agent.name)}
                    key={agent.name}
                  >
                    {agent.name}
                  </button>
                ))}
            </div>
          </div>

          <div className="manual-task-actions">
            <button className="btn-primary" type="submit" disabled={!manualTaskReady || assigningTask}>
              {assigningTask ? 'Starting...' : manualTaskReady ? 'Start agent task' : 'Add task text first'}
            </button>
            <span>{manualTaskBlocker || 'Creates work for the runner. It does not disappear unless claimed/completed.'}</span>
          </div>
        </form>
      </section>

      <section className="component-container projects-section">
        <div className="component-header">
          <div>
            <h2>Projects</h2>
            <p className="component-subtitle">
              Portfolio the CEO Agent oversees: goal, health, next action, and GA evidence where configured.
            </p>
          </div>
        </div>

        <div className="op-project-controls">
          <div className="op-tabs">
            {[
              ['all', 'All'],
              ['needs-attention', 'Needs attention'],
              ['high', 'High priority'],
              ['deployed', 'Deployed'],
              ['development', 'Development'],
              ['monitoring', 'Monitoring'],
            ].map(([value, label]) => (
              <button type="button" className={projectFilter === value ? 'active' : ''} onClick={() => setProjectFilter(value)} key={value}>{label}</button>
            ))}
          </div>
          <input
            value={projectSearch}
            onChange={(event) => setProjectSearch(event.target.value)}
            placeholder="Search projects, goals, domains, owners..."
            aria-label="Search projects"
          />
        </div>

        {projectsNeedingAttention.length ? (
          <p className="projects-attention-note">
            {projectsNeedingAttention.length} project{projectsNeedingAttention.length === 1 ? '' : 's'} need a goal or next action — the CEO Agent will raise these as Waiting for Z questions.
          </p>
        ) : null}

        {filteredProjects.length ? (
          <div className="op-project-grid">
            {filteredProjects.map((project) => (
              <OperatingProjectCard
                project={project}
                attention={attentionProjectIds.has(project.id)}
                dossier={projectDossiers.get(project.id)}
                analytics={analyticsByDomain.get(normalizeDomain(project.domain))}
                onOpen={(projectId) => {
                  setSelectedProjectId(projectId);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onAssign={prefillProjectTask}
                key={project.id}
              />
            ))}
          </div>
        ) : (
          <div className="empty-answer-work">
            No projects in the registry yet. The backend seeds them from the FIRMA inventory on next deploy.
          </div>
        )}

        <details className="project-add-drawer">
          <summary>Add a project</summary>
          <form className="manual-task-form project-add-form" onSubmit={addProject}>
            <label>
              Name
              <input
                value={newProject.name}
                onChange={(event) => updateNewProject('name', event.target.value)}
                placeholder="Required. Example: crypto-lab"
              />
            </label>
            <label>
              Domain
              <input
                value={newProject.domain}
                onChange={(event) => updateNewProject('domain', event.target.value)}
                placeholder="Optional. Example: cryptolab.app"
              />
            </label>
            <label className="manual-task-full">
              Goal
              <textarea
                value={newProject.goal}
                onChange={(event) => updateNewProject('goal', event.target.value)}
                placeholder="Optional. Empty means the CEO Agent will ask you for it."
                rows={2}
              />
            </label>
            <label>
              Priority
              <select value={newProject.priority} onChange={(event) => updateNewProject('priority', event.target.value)}>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
            </label>
            <label>
              Status
              <select value={newProject.status} onChange={(event) => updateNewProject('status', event.target.value)}>
                {PROJECT_STATUS_OPTIONS.map((status) => (
                  <option value={status} key={status}>{status}</option>
                ))}
              </select>
            </label>
            <div className="manual-task-actions">
              <button className="btn-primary" type="submit" disabled={!cleanText(newProject.name) || addingProject}>
                {addingProject ? 'Adding...' : 'Add project'}
              </button>
              <span>New projects land in the registry immediately; the CEO Agent picks them up on its next review.</span>
            </div>
          </form>
        </details>
      </section>

      <AgentLoadBoard agents={board.agents} activeWorkItems={activeWorkItems} pendingQuestions={pendingQuestions} />

      <details className="op-detail-drawer">
        <summary>Detailed live work map, linked next tasks, roster, and history</summary>

      <section className="component-container fable-work-map">
        <div className="component-header">
          <div>
            <h2>Live work map</h2>
            <p className="component-subtitle">
              Current context packets: where they came from, which agent owns them, who receives delegated context, and what is blocked.
            </p>
          </div>
        </div>

        <div className="fable-metrics-grid">
          <div className="fable-metric-card">
            <strong>{flowMetrics.active}</strong>
            <span>active packets</span>
          </div>
          <div className="fable-metric-card claimed">
            <strong>{flowMetrics.claimed}</strong>
            <span>claimed by runner</span>
          </div>
          <div className="fable-metric-card blocked">
            <strong>{flowMetrics.blocked}</strong>
            <span>blocked / waiting</span>
          </div>
          <div className="fable-metric-card done">
            <strong>{flowMetrics.completed}</strong>
            <span>recently completed</span>
          </div>
        </div>

        <div className="agent-lanes-grid">
          {activeAgentLanes.map((lane) => (
            <AgentLaneCard lane={lane} key={lane.agent.name} />
          ))}
        </div>

        <div className="context-flow-list">
          {activeWorkItems.length ? (
            activeWorkItems.map((item) => (
              <ContextFlowCard
                item={item}
                blocker={pendingByParentLog.get(item.id)}
                onFollowUp={startFollowUp}
                onCopyReport={copyHandoffReport}
                key={item.id}
              />
            ))
          ) : (
            <div className="empty-answer-work">
              No active context packets. Start a manual task or answer a Waiting for Z question to feed the runner.
            </div>
          )}
        </div>
      </section>

      <section className="component-container">
        <div className="component-header">
          <div>
            <h2>Agent roster</h2>
            <p className="component-subtitle">
              Core lanes stay visible. Specialist agents are available when the runner needs them.
            </p>
          </div>
        </div>

        <div className="agent-group-label">
          <span>Core agents</span>
          <strong>{coreAgents.length}</strong>
        </div>
        <div className="agents-grid core-agents-grid">
          {coreAgents.map((agent) => <AgentCard agent={agent} key={agent.name} />)}
        </div>

        {specialistAgents.length ? (
          <details className="specialist-agent-drawer">
            <summary>
              <span>Specialist agents</span>
              <strong>{specialistAgents.length} available</strong>
            </summary>
            <p>
              These are not always on-screen. The runner routes to them when it needs MCP/API wiring,
              security review, QA, UX, data, or research.
            </p>
            <div className="agents-grid specialist-agents-grid">
              {specialistAgents.map((agent) => <AgentCard agent={agent} key={agent.name} />)}
            </div>
          </details>
        ) : null}
      </section>

      <section className="component-container">
        <div className="component-header">
          <div>
            <h2>Linked next-task map</h2>
            <p className="component-subtitle">Only server-created same-agent continuations and specialist handoffs appear here.</p>
          </div>
        </div>
        {liveHandoffs.length ? (
          <div className="live-handoff-list">
            {liveHandoffs.map((item) => (
              <div className="delegation-row live" key={`live-${item.id}`}>
                <LinkedHandoffRoute item={item} />
                <p>{item.routingReason || handoffReportFor(item).nextAction || item.message || item.workingOn}</p>
                <RunnerBadge status={runnerStatus(item)} />
              </div>
            ))}
          </div>
        ) : null}
        {!liveHandoffs.length ? <p className="empty-state">No server-linked next tasks yet.</p> : null}
      </section>

      <section className="component-container agent-log-section">
        <details className="agent-history-drawer">
          <summary>
            <span>Recent completed context</span>
            <strong>{recentCompletedItems.length} shown / {agentLog.length} total</strong>
          </summary>
          <p>
            Full answered/manual-task history stays here. Current work lives in the Live work map above.
          </p>

          {recentCompletedItems.length ? (
            <div className="agent-log-list">
              {recentCompletedItems.map((item) => {
                const runner = item.runner || { status: 'completed' };
                const resultSummary = runner.result?.summary || runner.result?.message || '';
                return (
                  <article className="agent-log-card compact-history" key={item.id}>
                    <div className="answer-work-header">
                      <div>
                        <span className="question-owner">{item.agent}</span>
                        <h3>{item.question}</h3>
                      </div>
                      <RunnerBadge status={runner.status || 'completed'} />
                    </div>
                    {resultSummary ? (
                      <HandoffReportCard item={item} compact onCopy={copyHandoffReport} />
                    ) : (
                      <>
                        <p className="result-missing-warning">
                          Completed without a result — use Follow up to re-run with a required summary.
                        </p>
                        <p>{shortText(item.message, 220)}</p>
                      </>
                    )}
                    <div className="followup-actions">
                      <button type="button" className="followup-btn" onClick={() => startFollowUp(item)}>
                        Follow up
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-answer-work">
              No completed runner history yet.
            </div>
          )}
        </details>
      </section>

      </details>

      <section className="component-container answer-bucket">
        <div className="component-header">
          <div>
            <h2>Waiting for Z</h2>
            <p className="component-subtitle">
              Type freely here. The window will stay open until you press that question’s Return to agent button.
            </p>
          </div>
        </div>

        {pendingQuestions.length ? (
          <div className="questions-list">
            {pendingQuestions.map((item) => {
              const draftValue = draftAnswers[item.id] || '';
              const canReturn = Boolean(cleanText(draftValue));
              const isReturning = returningId === item.id;
              return (
                <article className="question-card" id={safeDomId('question', item.id)} key={item.id}>
                  <div className="question-card-header">
                    <div>
                      <span className="question-owner">{item.owner}</span>
                      <h3>{item.question}</h3>
                    </div>
                    <span className={`question-priority ${(item.priority || 'medium').toLowerCase()}`}>{item.priority || 'Medium'}</span>
                  </div>
                  <p><strong>Why:</strong> {item.why}</p>
                  <p><strong>Recommendation:</strong> {item.recommendation}</p>
                  <label htmlFor={`answer-${item.id}`}>Z answer</label>
                  <textarea
                    id={`answer-${item.id}`}
                    value={draftValue}
                    onChange={(event) => updateDraft(item.id, event.target.value)}
                    placeholder="Type answer / decision here. It will not leave this window until you return it to the agent."
                    rows={3}
                  />
                  <div className="question-card-actions">
                    <button
                      className="btn-primary answer-return-button"
                      type="button"
                      onClick={() => returnToAgent(item)}
                      disabled={!canReturn || isReturning}
                    >
                      {isReturning ? 'Returning...' : 'Return to agent'}
                    </button>
                    <span className="draft-hint">
                      Draft only. Returns to {item.owner}; it disappears only after that succeeds.
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="empty-answer-work">
            Nothing waiting for you. Agents have the current answers and should continue from their logs.
          </div>
        )}

        <div className="component-footer">
          <p className="text-small">
            State is backend-persisted on the mounted Dokploy volume. The browser cache is only a fallback.
          </p>
        </div>
      </section>
    </div>
  );
}

export default AgentWorkspace;
